import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Web as WebIcon,
  DragIndicator as DragIndicatorIcon,
  FilterList as FilterListIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  pointerWithin,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Função para obter o nome do estágio baseado na configuração das variáveis de ambiente
const getStageName = (stageOrder: number | string, stagesConfig: any): string => {
  if (!stagesConfig) return 'Estágio';
  
  // Converter stageOrder para número se for string (ex: "6.5")
  const orderNum = typeof stageOrder === 'string' ? parseFloat(stageOrder) : stageOrder;
  
  // Mapeamento correto: stage_order → stageConfig
  // stage_order 6.5 é tratado como caso especial
  if (orderNum === 6.5 || stageOrder === '6.5' || stageOrder === 6.5) {
    return stagesConfig.stage6_5?.name || 'Pré-Negociação';
  }
  
  const stageMap: Record<number, string> = {
    1: stagesConfig.stage1?.name || 'Lead Novo',
    2: stagesConfig.stage2?.name || 'Contato Feito',
    3: stagesConfig.stage3?.name || 'Interessado',
    4: stagesConfig.stage4?.name || 'Depósito Realizado',
    5: stagesConfig.stage5?.name || 'Cliente',
    6: stagesConfig.stage6?.name || 'Perdido',
    7: stagesConfig.stage7?.name || 'Negociação',
    8: stagesConfig.stage8?.name || 'Fechamento',
    9: stagesConfig.stage9?.name || 'Estágio 9',
    10: stagesConfig.stage10?.name || 'Estágio 10',
    11: stagesConfig.stage11?.name || 'Estágio 11'
  };
  
  return stageMap[orderNum] || `Estágio ${stageOrder}`;
};

import { getApiUrl } from '../config/environment';
import { 
  getTodayDates, 
  getPeriodParams, 
  formatBrazilDate, 
  isToday,
  PeriodType 
} from '../utils/dateFilters';
import { PeriodFilter } from '../components/ui/PeriodFilter';
import LeadContactTags from '../components/Inbox/LeadContactTags';

const API_BASE_URL = getApiUrl();

/** Lembra a última pipeline vista no CRM ao recarregar a página. */
const CRM_SELECTED_PIPELINE_KEY = 'crm_selected_pipeline_name';

const CRM_STAGE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizeStageOrderValue(order: unknown): number | string {
  if (order === null || order === undefined) return '';
  if (typeof order === 'number') return order;
  const n = parseFloat(String(order));
  return Number.isFinite(n) ? n : String(order);
}

function getLeadPipelineName(lead: any): string | undefined {
  const p = lead?.crm_pipelines;
  if (!p) return undefined;
  const row = Array.isArray(p) ? p[0] : p;
  return row?.name;
}

function stagesScopedToPipeline(pipe: { id?: string; stages?: any[] } | null | undefined): any[] {
  if (!pipe?.stages?.length) return [];
  const pid = pipe.id;
  if (!pid) return [...pipe.stages];
  return pipe.stages.filter((s: any) => s.pipeline_id === pid);
}

// Função para obter o ícone baseado na fonte do lead
const getChannelIcon = (source: string) => {
  switch (source?.toLowerCase()) {
    case 'telegram':
      return <TelegramIcon sx={{ fontSize: 16, color: '#229ED9' }} />;
    case 'whatsapp':
      return <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />;
    case 'instagram':
      return <InstagramIcon sx={{ fontSize: 16, color: '#E4405F' }} />;
    case 'webchat':
      return <WebIcon sx={{ fontSize: 16, color: '#6366F1' }} />;
    default:
      return null;
  }
};

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  value: number;
  currency?: string; // 'BRL' ou 'USD'
  tags: Array<{ id: string; name: string; color: string | null }>;
  avatar: string | null;
  notes: string;
  lastContact: string;
  source?: string;
  stageId?: string;
  enteredAt?: string;
  custom_fields?: any;
}

interface KanbanColumn {
  id: string;
  title: string;
  color: string;
  count: number;
  contacts: Contact[];
  /** Ordem no banco (crm_stages.stage_order); Kanban por UUID usa isto para agrupar leads */
  stageOrder?: number | string;
  isFinalStage?: boolean;
}

// Componente para o card arrastável - otimizado com React.memo
const DraggableContactCard: React.FC<{
  contact: Contact;
  column: KanbanColumn;
  onMoveToNextStage: (contactId: string) => void;
  onContactClick: (contact: Contact) => void;
}> = React.memo(({ contact, column, onMoveToNextStage, onContactClick }) => {
  const { mode } = useCustomTheme();
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const API_BASE_URL = getApiUrl();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const openInNewTab = (path: string) => {
    const fullPath = path.startsWith('/') ? path : `/${path}`;
    window.open(`${window.location.origin}${fullPath}`, '_blank', 'noopener,noreferrer');
  };

  const handleViewConversation = async () => {
    handleMenuClose();
    const contactParam = `?contactId=${contact.id}`;
    if (contact.source) {
      // Se for WhatsApp, abrir conversa via API primeiro
      if (contact.source.toLowerCase() === 'whatsapp') {
        try {
          const response = await axios.post(`/api/v1/n8n/leads/${contact.id}/open-whatsapp`, {
            message: 'Olá! Como posso ajudá-lo hoje?'
          });
          if (response.data.success) {
            console.log('WhatsApp conversation opened:', response.data);
          }
        } catch (error) {
          console.error('Error opening WhatsApp conversation:', error);
        }
        openInNewTab(`/inbox/whatsapp${contactParam}`);
        return;
      }
      switch (contact.source.toLowerCase()) {
        case 'telegram':
          openInNewTab(`/inbox/telegram${contactParam}`);
          break;
        case 'instagram':
          openInNewTab(`/inbox/instagram${contactParam}`);
          break;
        case 'webchat':
          openInNewTab(`/inbox/webchat${contactParam}`);
          break;
        case 'sms':
          openInNewTab(`/campaigns/sms${contactParam}`);
          break;
        case 'voice':
          openInNewTab(`/campaigns/voice${contactParam}`);
          break;
        default:
          openInNewTab(`/inbox${contactParam}`);
      }
    } else {
      openInNewTab(`/inbox${contactParam}`);
    }
  };

  const handleOpenWhatsApp = async () => {
    handleMenuClose();
    
    // Verificar se o lead tem telefone
    if (!contact.phone) {
      alert('Este lead não possui número de telefone cadastrado. Adicione um telefone para abrir conversa no WhatsApp.');
      return;
    }
    
    try {
      // Chamar API para abrir conversa WhatsApp sem enviar mensagem
      const response = await axios.post(`/api/v1/n8n/leads/${contact.id}/open-whatsapp`, {
        message: null
      });
      
      if (response.data.success) {
        console.log('WhatsApp conversation opened:', response.data);
        const contactParam = `?contactId=${contact.id}`;
        openInNewTab(`/inbox/whatsapp${contactParam}`);
      } else {
        console.error('Failed to open WhatsApp conversation:', response.data.error);
        alert('Erro ao abrir conversa WhatsApp: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error opening WhatsApp conversation:', error);
      alert('Erro ao abrir conversa WhatsApp. Tente novamente.');
    }
  };

  const handleDeleteLead = async () => {
    handleMenuClose();
    
    const contactId = contact.id;
    const contactName = contact.name || 'este lead';
    
    // Confirmar exclusão
    if (window.confirm(`Tem certeza que deseja excluir o lead "${contactName}"? Esta ação não pode ser desfeita.`)) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/contacts/${contactId}`);
        
        if (response.data.success) {
          console.log('Lead deleted successfully:', response.data);
          
          // Chamar callback para atualizar a lista no componente pai
          // O componente pai precisa atualizar allLeadsData e reaplicar filtros
          window.location.reload(); // Recarregar página para garantir atualização completa
        } else {
          console.error('Failed to delete lead:', response.data.error);
          alert(response.data.error || 'Erro ao excluir o lead. Tente novamente.');
        }
      } catch (error: any) {
        console.error('Error deleting lead:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Erro ao excluir o lead. Tente novamente.';
        alert(errorMessage);
      }
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: contact.id,
    data: {
      type: 'contact',
      contact: contact
    }
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1, // Opacidade durante drag para melhor feedback visual
    // Otimizações de performance - GPU acceleration
    willChange: isDragging ? 'transform' : 'auto',
    pointerEvents: isDragging ? ('none' as const) : ('auto' as const), // Desabilitar interações durante drag
    // Forçar aceleração de hardware
    transformOrigin: 'center center',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
  };

  const formatCurrency = (value: number, currency: string = 'BRL') => {
    const locale = currency === 'USD' ? 'en-US' : 'pt-BR';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  const displayName = contact.name || contact.email || contact.phone || 'Lead';
  const initial = displayName.charAt(0).toUpperCase();

  const tags = Array.isArray(contact.tags) ? contact.tags : [];

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevenir clique se estiver arrastando
    if (isDragging) return;
    onContactClick(contact);
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      className="sophisticated-card"
      sx={{
        p: 2,
        cursor: isDragging ? 'grabbing' : 'grab',
        background: isDragging ? 'transparent' : '#ffffff',
        border: isDragging ? 'none' : '1px solid rgba(139, 92, 246, 0.2)',
        borderRadius: 4,
        boxShadow: isDragging ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(139, 92, 246, 0.1)',
        transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        // Otimizações de performance - GPU acceleration
        willChange: isDragging ? 'transform' : 'auto',
        transform: isDragging ? 'translateZ(0) scale(0.98)' : 'translateZ(0)',
        // Remover todos os efeitos pesados durante drag e sempre
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        ...(isDragging ? {
          pointerEvents: 'none',
          userSelect: 'none',
        } : {}),
        // Forçar aceleração de hardware
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        perspective: 1000,
        '&:hover': {
          ...(isDragging ? {} : {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(20, 184, 166, 0.15)',
            borderColor: 'rgba(20, 184, 166, 0.3)',
            background: '#ffffff',
          }),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent)',
        },
        '&:active': {
          cursor: 'grabbing',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{ 
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <DragIndicatorIcon 
            sx={{ 
              color: 'text.secondary', 
              fontSize: 16
            }} 
          />
        </Box>
        <Avatar
          src={contact.avatar || undefined}
          sx={{
            bgcolor: contact.avatar ? 'transparent' : column.color + '15',
            color: column.color,
            fontWeight: 600,
            width: 32,
            height: 32,
            fontSize: '0.8rem'
          }}
        >
          {!contact.avatar && initial}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {displayName}
            </Typography>
            {contact.source && (
              <Box sx={{ ml: 0.5 }}>
                {getChannelIcon(contact.source)}
              </Box>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {(contact.email || contact.phone || 'Lead').slice(0, 20)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000', mb: 0.5, fontSize: '0.95rem' }}>
          {formatCurrency(contact.value, contact.currency || contact.custom_fields?.currency || 'BRL')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
          {tags.slice(0, 2).map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              sx={{
                borderLeft: '2px solid',
                borderColor: tag.color || '#94a3b8',
                bgcolor: column.color + '10',
                color: column.color,
                fontSize: '0.6rem',
                height: 16,
                fontWeight: 500,
                '& .MuiChip-label': {
                  px: 0.5
                }
              }}
            />
          ))}
          {tags.length > 2 && (
            <Chip
              label={`+${tags.length - 2}`}
              size="small"
              sx={{
                bgcolor: '#E2E8F0',
                color: 'text.secondary',
                fontSize: '0.6rem',
                height: 16,
                fontWeight: 500,
                '& .MuiChip-label': {
                  px: 0.5
                }
              }}
            />
          )}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ 
        mb: 1,
        fontSize: '0.7rem',
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical'
      }}>
        {contact.notes}
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pt: 0.5,
        borderTop: '1px solid #F1F5F9'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {new Date(contact.lastContact).toLocaleDateString('pt-BR')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {column.id !== 'ganho' && (
            <Tooltip title="Mover para próximo estágio">
              <IconButton 
                size="small" 
                sx={{ 
                  color: 'text.secondary',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: column.color + '10',
                    color: column.color
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToNextStage(contact.id);
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton 
            size="small" 
            onClick={handleMenuOpen}
            sx={{ 
              color: 'text.secondary',
              p: 0.5,
              '&:hover': {
                bgcolor: column.color + '10',
                color: column.color
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      {/* Menu de opções do lead */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(139, 92, 246, 0.08) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(139, 92, 246, 0.18)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(139, 92, 246, 0.05)',
            borderRadius: 2,
          }
        }}
      >
        <MenuItem onClick={handleViewConversation} sx={{ py: 1.5 }}>
          <ChatIcon sx={{ mr: 1.5, fontSize: 18, color: '#6366F1' }} />
          <Typography variant="body2">Ver Conversa</Typography>
        </MenuItem>
        <MenuItem onClick={handleOpenWhatsApp} sx={{ py: 1.5 }}>
          <WhatsAppIcon sx={{ mr: 1.5, fontSize: 18, color: '#25D366' }} />
          <Typography variant="body2">Abrir WhatsApp</Typography>
        </MenuItem>
        <MenuItem onClick={() => navigate(`/lead-details/${contact.id}`)} sx={{ py: 1.5 }}>
          <PersonIcon sx={{ mr: 1.5, fontSize: 18, color: '#14b8a6' }} />
          <Typography variant="body2">Ver Detalhes</Typography>
        </MenuItem>
        <MenuItem onClick={handleDeleteLead} sx={{ py: 1.5, color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1.5, fontSize: 18, color: 'error.main' }} />
          <Typography variant="body2" color="error">Apagar Lead</Typography>
        </MenuItem>
      </Menu>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Função de comparação customizada: retorna true se props são iguais (não re-renderizar)
  // Retorna false se props são diferentes (deve re-renderizar)
  return (
    prevProps.contact.id === nextProps.contact.id &&
    prevProps.contact.stageId === nextProps.contact.stageId &&
    prevProps.contact.name === nextProps.contact.name &&
    prevProps.contact.email === nextProps.contact.email &&
    prevProps.contact.phone === nextProps.contact.phone &&
    prevProps.contact.value === nextProps.contact.value &&
    prevProps.column.id === nextProps.column.id &&
    prevProps.onMoveToNextStage === nextProps.onMoveToNextStage &&
    prevProps.onContactClick === nextProps.onContactClick
  );
});

// Componente para área de drop da coluna
const DroppableColumn: React.FC<{
  column: KanbanColumn;
  children: (isOver: boolean) => React.ReactNode;
}> = ({ column, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      column: column
    }
  });

  // Removido console.log para melhor performance

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: { xs: 'none', md: 1 },
        width: { xs: '100%', md: 'auto' },
        minWidth: { xs: 'auto', md: 250 },
        maxWidth: { xs: '100%', md: 250 },
        background: isOver 
          ? 'rgba(139, 92, 246, 0.15)'
          : 'transparent',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        borderRadius: 4,
        border: isOver 
          ? '2px solid rgba(139, 92, 246, 0.5)'
          : '1px solid rgba(139, 92, 246, 0.25)',
        boxShadow: isOver 
          ? '0 12px 24px rgba(139, 92, 246, 0.25), 0 6px 12px rgba(139, 92, 246, 0.15)'
          : '0 6px 16px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(139, 92, 246, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: isOver ? 'none' : 'all 0.15s ease',
        transform: isOver ? 'scale(1.02) translateZ(0)' : 'scale(1)',
        minHeight: { xs: '300px', md: '500px' },
        mb: { xs: 1, md: 0 },
        overflow: 'visible',
        willChange: isOver ? 'transform' : 'auto',
        '&:hover': {
          ...(isOver ? {} : {
            transform: 'scale(1.005)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
          }),
        },
      }}
    >
      {children(isOver)}
    </Box>
  );
};

// Componente para área vazia da coluna
const EmptyColumnArea: React.FC<{ isOver: boolean }> = ({ isOver }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: 120,
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'background.default',
      color: 'text.secondary',
      transition: 'all 0.2s ease-in-out',
      ...(isOver && {
        borderColor: '#3B82F6',
        bgcolor: 'rgba(139, 92, 246, 0.05)',
        color: '#3B82F6'
      })
    }}
  >
    <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ fontSize: '0.75rem' }}>
      {isOver ? 'Solte aqui para mover o lead' : 'Nenhum lead neste estágio'}
    </Typography>
  </Box>
);

// Função para mapear estágios da API para o frontend
const mapStageToColumn = (stageName: string, stageOrder: number) => {
  switch (stageOrder) {
    case 1:
      return 'new-lead';
    case 2:
      return 'grupo';
    case 3:
      return 'cadastro';
    case 4:
      return 'ganho';
    case 5:
      return 'perdido';
    case 6:
      return 'followup';
    case 7:
      return 'pre-negociacao';
    case 8:
      return 'negociacao';
    case 9:
      return 'fechamento';
    default:
      return 'new-lead';
  }
};

// Função para obter o ID do estágio baseado no nome da coluna
const getStageIdFromColumn = (columnId: string, leads: any[]) => {
  console.log('getStageIdFromColumn called with:', { columnId, leadsCount: leads.length });
  
  // Mapeamento direto de coluna para ordem do estágio
  const columnToOrder: { [key: string]: number | string } = {
    'new-lead': 1,
    'grupo': 2,
    'cadastro': 3,
    'ganho': 4,
    'perdido': 5,
    'followup': 6,
    'pre-negociacao': 6.5,
    'negociacao': 7,
    'fechamento': 8,
    'stage9': 9,
    'stage10': 10,
    'stage11': 11
  };
  
  const targetOrder = columnToOrder[columnId as keyof typeof columnToOrder];
  if (!targetOrder) {
    console.log('Invalid column ID:', columnId);
    return null;
  }
  
  // Buscar por um lead que esteja no estágio com a ordem correta
  const targetLead = leads.find(lead => {
    const stageOrder = lead.crm_stages?.stage_order;
    console.log('Checking lead:', lead.id, 'stage order:', stageOrder, 'target:', targetOrder);
    return stageOrder === targetOrder;
  });
  
  if (!targetLead) {
    console.log('No lead found for stage order:', targetOrder);
    // Se não encontrar um lead, vamos buscar diretamente nos estágios
    const allStages = Array.from(new Set(leads.map(lead => lead.crm_stages?.id).filter(Boolean)));
    console.log('Available stage IDs:', allStages);
    return null;
  }
  
  const stageId = targetLead.crm_stages?.id;
  console.log('Stage ID for column', columnId, ':', stageId, 'from lead:', targetLead.id);
  
  return stageId;
};

// Template das colunas do Kanban (será atualizado dinamicamente)
const defaultKanbanColumnsTemplate: KanbanColumn[] = [
  {
    id: 'new-lead',
    title: 'Novo Lead',
    color: 'text.secondary',
    count: 0,
    contacts: [],
  },
  {
    id: 'grupo',
    title: 'Grupo',
    color: '#3B82F6',
    count: 0,
    contacts: [],
  },
  {
    id: 'cadastro',
    title: 'Cadastro',
    color: '#F59E0B',
    count: 0,
    contacts: [],
  },
  {
    id: 'ganho',
    title: 'Ganho',
    color: '#10B981',
    count: 0,
    contacts: [],
  },
  {
    id: 'perdido',
    title: 'Perdido',
    color: '#EF4444',
    count: 0,
    contacts: [],
  },
  {
    id: 'followup',
    title: 'Follow-up',
    color: '#8B5CF6',
    count: 0,
    contacts: [],
  },
  {
    id: 'pre-negociacao',
    title: 'Pré-Negociação',
    color: '#9C27B0',
    count: 0,
    contacts: [],
  },
  {
    id: 'negociacao',
    title: 'Negociação',
    color: '#F97316',
    count: 0,
    contacts: [],
  },
  {
    id: 'fechamento',
    title: 'Fechamento',
    color: '#06B6D4',
    count: 0,
    contacts: [],
  },
];

function resolveKanbanColumnOrder(column: KanbanColumn): number | string | undefined {
  const legacyMap: Record<string, number | string> = {
    'new-lead': 1,
    grupo: 2,
    cadastro: 3,
    ganho: 4,
    perdido: 5,
    followup: 6,
    'pre-negociacao': 6.5,
    negociacao: 7,
    fechamento: 8,
    stage9: 9,
    stage10: 10,
    stage11: 11,
  };
  const raw =
    column.stageOrder !== undefined && column.stageOrder !== null && column.stageOrder !== ''
      ? column.stageOrder
      : legacyMap[column.id];
  if (raw === undefined || raw === '') return undefined;
  if (typeof raw === 'number') return raw;
  const s = String(raw);
  return s.includes('.') ? parseFloat(s) : parseInt(s, 10);
}

function buildKanbanColumnsFromStages(stages: any[]): KanbanColumn[] {
  if (!stages?.length) return [...defaultKanbanColumnsTemplate];
  const sorted = [...stages].sort((a, b) => {
    const ao = Number(normalizeStageOrderValue(a.stage_order));
    const bo = Number(normalizeStageOrderValue(b.stage_order));
    if (!Number.isNaN(ao) && !Number.isNaN(bo)) return ao - bo;
    return String(a.stage_order).localeCompare(String(b.stage_order));
  });
  return sorted.map((s) => ({
    id: s.id,
    title: s.name || `Estágio ${s.stage_order}`,
    color: s.color || '#6B7280',
    count: 0,
    contacts: [],
    stageOrder: normalizeStageOrderValue(s.stage_order),
    isFinalStage: !!s.is_final_stage,
  }));
}

const CRM: React.FC = () => {
  const { mode } = useCustomTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [openLeadDetailsDialog, setOpenLeadDetailsDialog] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    value: '',
    notes: '',
    pipelineName: '',
    stage: '',
    tagIds: [] as string[],
  });
  const [kanbanColumns, setKanbanColumns] = useState<KanbanColumn[]>(defaultKanbanColumnsTemplate);
  const [stagesConfig, setStagesConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [selectedPipelineName, setSelectedPipelineName] = useState('');
  const pipelineNameRef = useRef<string>('Funil de Vendas');
  const [openNewPipelineDialog, setOpenNewPipelineDialog] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [newPipelineDescription, setNewPipelineDescription] = useState('');
  const [newPipelineColor, setNewPipelineColor] = useState('#3B82F6');
  const [creatingPipeline, setCreatingPipeline] = useState(false);
  const [openEditPipelineStagesDialog, setOpenEditPipelineStagesDialog] = useState(false);
  const [savingPipelineStageNames, setSavingPipelineStageNames] = useState(false);
  const [pipelineStagesEditRows, setPipelineStagesEditRows] = useState<
    Array<{ id: string; stage_order: number | string }>
  >([]);
  const [pipelineStageNameDrafts, setPipelineStageNameDrafts] = useState<Record<string, string>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedContact, setDraggedContact] = useState<Contact | null>(null);
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [allLeadsData, setAllLeadsData] = useState<any[]>([]); // Armazena todos os leads antes da filtragem
  const [allStages, setAllStages] = useState<any[]>([]); // Armazena todos os estágios disponíveis
  const [isMovingContact, setIsMovingContact] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showStageIndicator, setShowStageIndicator] = useState<boolean>(false);
  const [kanbanContainerRef, setKanbanContainerRef] = useState<HTMLDivElement | null>(null);
  const [cardsContainerRef, setCardsContainerRef] = useState<HTMLDivElement | null>(null);
  const [scrollBarRef, setScrollBarRef] = useState<HTMLDivElement | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState({
    source: 'all',
    hasEmail: null as boolean | null,
    hasPhone: null as boolean | null,
    hasNotes: null as boolean | null,
    lastContactDays: null as number | null,
    dateFrom: '',
    dateTo: '',
    tagIds: [] as string[],
  });
  const [crmCatalogTags, setCrmCatalogTags] = useState<
    Array<{ id: string; name: string; color: string | null }>
  >([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Estados para filtro de período (padrão: Hoje para carregar mais rápido)
  const [period, setPeriod] = useState<PeriodType>('today');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const sortStagesForPicker = (stages: any[]) =>
    [...stages].sort((a, b) => {
      const ao = Number(normalizeStageOrderValue(a.stage_order));
      const bo = Number(normalizeStageOrderValue(b.stage_order));
      if (!Number.isNaN(ao) && !Number.isNaN(bo)) return ao - bo;
      return String(a.stage_order).localeCompare(String(b.stage_order));
    });

  const newLeadPipelineStages = useMemo(() => {
    const p = pipelines.find((x: any) => x.name === contactForm.pipelineName);
    const scoped = stagesScopedToPipeline(p);
    if (!scoped.length) return [];
    return sortStagesForPicker(scoped);
  }, [pipelines, contactForm.pipelineName]);

  // Configuração dos sensores para drag and drop - otimizado para performance
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // Ativar após 3px de movimento
      },
    })
  );
  
  // Cache para encontrar contatos rapidamente durante drag
  const contactsMap = useMemo(() => {
    const map = new Map<string, Contact>();
    kanbanColumns.forEach(col => {
      col.contacts.forEach(contact => {
        map.set(contact.id, contact);
      });
    });
    return map;
  }, [kanbanColumns]);

  // Função para buscar estatísticas do CRM
  const fetchCrmStats = async (channelFilterParam?: string) => {
    try {
      const periodParams = getPeriodParams(period, dateFrom, dateTo);
      const params = new URLSearchParams(periodParams);
      
      // ✅ Adiciona filtro de canal se fornecido
      const filterToUse = channelFilterParam !== undefined ? channelFilterParam : channelFilter;
      if (filterToUse && filterToUse !== 'all') {
        params.append('source', filterToUse); // Envia 'whatsapp', 'telegram', etc.
      }
      
      console.log('🔍 Debug CRM Stats - Parâmetros:', periodParams, 'Filtro de canal:', filterToUse);
      
      // Tentar buscar do cache primeiro (mais rápido)
      let response;
      try {
        console.log('🔍 CRM Stats: Tentando buscar do cache...');
        response = await Promise.race([
          axios.get(`${API_BASE_URL}/crm/cache/stats?${params}`, { timeout: 10000 }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000))
        ]) as any;
        console.log('📦 CRM Stats: Dados obtidos do cache', response.data.message);
      } catch (cacheError: any) {
        // Se cache falhar, usar endpoint normal
        console.log('🔄 CRM Stats: Cache não disponível ou timeout, usando endpoint normal', cacheError?.message || cacheError);
        try {
          response = await axios.get(`${API_BASE_URL}/crm/stats?${params}`, { timeout: 25000 });
          console.log('✅ CRM Stats: Dados obtidos do endpoint normal');
        } catch (normalError: any) {
          console.error('❌ CRM Stats: Erro em ambos os endpoints:', normalError?.message || normalError);
          throw normalError;
        }
      }
      
      if (response.data.success) {
        console.log('📊 CRM Stats Response:', response.data.data);
        console.log('📈 Stage Stats:', response.data.data.stageStats);
        console.log('🔢 Total Leads:', response.data.data.totalLeads);
        
        // Verificar se há limitação nos dados
        const totalStageCount = response.data.data.stageStats?.reduce((sum: number, stage: any) => sum + (stage.count || 0), 0) || 0;
        console.log('🧮 Soma dos estágios:', totalStageCount);
        
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Erro ao buscar estatísticas do CRM:', error);
      return null;
    }
  };

  // Cache para otimizar performance com milhares de leads
  const [leadsCache, setLeadsCache] = useState<{
    data: any[];
    timestamp: number;
    filters: string;
  } | null>(null);
  
  const CACHE_DURATION = 2 * 60 * 1000; // 2 minutos para milhares de leads
  
  // Estados para paginação visual - mostrar menos cards para melhor performance
  const [cardsPerColumn, setCardsPerColumn] = useState<{ [stageId: string]: number }>({});
  const [isLoadingMore, setIsLoadingMore] = useState<{ [stageId: string]: boolean }>({});
  const INITIAL_CARDS_PER_COLUMN = 8; // Mostrar apenas 8 cards inicialmente
  const CARDS_TO_LOAD_MORE = 15; // Carregar mais 15 cards por vez

  // Função para carregar mais cards em uma coluna específica
  const loadMoreCards = useCallback((stageId: string) => {
    setIsLoadingMore(prev => ({ ...prev, [stageId]: true }));
    
    // Usar requestAnimationFrame para melhor performance
    requestAnimationFrame(() => {
      setTimeout(() => {
        setCardsPerColumn(prev => ({
          ...prev,
          [stageId]: (prev[stageId] || INITIAL_CARDS_PER_COLUMN) + CARDS_TO_LOAD_MORE
        }));
        setIsLoadingMore(prev => ({ ...prev, [stageId]: false }));
      }, 150); // Reduzido delay
    });
  }, []);

  // Memoizar cards visíveis por coluna para evitar recálculos
  const visibleCardsByColumn = useMemo(() => {
    const result: { [stageId: string]: Contact[] } = {};
    kanbanColumns.forEach(column => {
      const limit = cardsPerColumn[column.id] || INITIAL_CARDS_PER_COLUMN;
      result[column.id] = column.contacts.slice(0, limit);
    });
    return result;
  }, [kanbanColumns, cardsPerColumn]);

  // Função para obter cards visíveis de uma coluna (usando cache memoizado)
  const getVisibleCards = useCallback((contacts: Contact[], stageId: string) => {
    return visibleCardsByColumn[stageId] || contacts.slice(0, INITIAL_CARDS_PER_COLUMN);
  }, [visibleCardsByColumn]);

  // Resetar paginação quando filtros mudarem
  useEffect(() => {
    setCardsPerColumn({});
    setIsLoadingMore({});
  }, [period, dateFrom, dateTo]);

  // Função para buscar leads da API com filtros avançados e cache inteligente
  // Quando há busca por nome, ignora o filtro de período (hoje/etc) e busca em todos os leads
  const fetchLeads = async (searchTerm?: string, filters?: any, opts?: { pipeline?: string }) => {
    try {
      // Se período é custom mas as datas não foram selecionadas, não fazer requisição (exceto quando há busca por nome)
      const hasSearch = !!(searchTerm && searchTerm.trim().length > 0);
      if (!hasSearch && period === 'custom' && !dateFrom && !dateTo) {
        return;
      }

      setLoading(true);
      setError('');
      
      const pipelineForRequest = opts?.pipeline ?? pipelineNameRef.current ?? 'Funil de Vendas';

      // Criar chave única para cache: quando há busca, o período é ignorado na API, então não entra na chave
      const hasSearchForCache = !!(searchTerm && searchTerm.trim().length > 0);
      const cacheKey = hasSearchForCache
        ? JSON.stringify({ pipeline: pipelineForRequest, searchTerm, filters, period: 'general' })
        : JSON.stringify({ pipeline: pipelineForRequest, searchTerm, filters, period, dateFrom, dateTo });
      const now = Date.now();
      
      // Verificar se temos dados em cache válidos
      if (leadsCache && 
          leadsCache.timestamp + CACHE_DURATION > now && 
          leadsCache.filters === cacheKey) {
        console.log('📦 CRM: Usando dados do cache local');
        setAllLeadsData(leadsCache.data);
        setLoading(false);
        return;
      }
      console.log('🔄 CRM: Buscando dados frescos da API');
      
      // Construir parâmetros da query
      const baseParams = {
        ...(searchTerm && { search: searchTerm }),
        ...(filters && filters.source && filters.source !== 'all' && { source: filters.source }),
        ...(filters && filters.hasEmail !== null && { hasEmail: filters.hasEmail.toString() }),
        ...(filters && filters.hasPhone !== null && { hasPhone: filters.hasPhone.toString() }),
        ...(filters && filters.hasNotes !== null && { hasNotes: filters.hasNotes.toString() }),
        ...(filters && filters.lastContactDays && { lastContactDays: filters.lastContactDays.toString() }),
        ...(filters && filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters && filters.dateTo && { dateTo: filters.dateTo }),
        ...(filters?.tagIds?.length && { tag_ids: filters.tagIds.join(',') }),
      };

      // Quando há busca por nome, ignorar filtro de período para buscar em TODOS os leads
      const hasSearchTerm = !!(searchTerm && searchTerm.trim().length > 0);
      let periodParams: Record<string, string> = {};
      if (!hasSearchTerm) {
        periodParams = getPeriodParams(period, dateFrom, dateTo);
        if (period === 'custom' && Object.keys(periodParams).length === 0) {
          setLoading(false);
          return;
        }
      }
      
      // Debug: Verificar parâmetros de período
      console.log('🔍 Debug CRM - Filtros de período:');
      console.log('Period:', period);
      console.log('HasSearchTerm:', hasSearchTerm);
      console.log('PeriodParams:', hasSearchTerm ? '(ignorado - busca ativa)' : periodParams);
      
      const params = new URLSearchParams({
        ...baseParams,
        ...periodParams,
      });
      params.set('pipeline', pipelineForRequest);
      console.log('🔍 CRM: Buscando leads da API:', `${API_BASE_URL}/crm/all-leads?${params}`);
      const response = await axios.get(`${API_BASE_URL}/crm/all-leads?${params}`);
      
      console.log('📡 CRM: Resposta da API:', response.data);
      
      if (!response.data.success) {
        console.error('❌ CRM: API retornou success=false:', response.data);
        throw new Error('Falha ao buscar leads');
      }
      
      const leads = response.data.data || [];
      console.log('✅ CRM: Leads recebidos da API:', leads.length, 'leads');
      console.log('📋 CRM: Primeiros 3 leads:', leads.slice(0, 3));
      console.log('Applied filters:', response.data.filters || 'No filters returned');
      
      // Validar que os leads não são mock
      if (leads.length === 0) {
        console.warn('⚠️ CRM: Nenhum lead retornado da API');
      }
      
      // Armazenar todos os leads para filtragem
      setAllLeadsData(leads);
      
      // Armazenar no cache local
      setLeadsCache({
        data: leads,
        timestamp: now,
        filters: cacheKey
      });
      
      console.log(`📊 CRM: Carregados ${leads.length} leads da API e salvos no cache`);
      
      // Não aplicar filtro aqui - será aplicado pelo useEffect quando stagesConfig estiver carregado
    } catch (err) {
      console.error('Erro ao buscar leads:', err);
      setError('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar filtros aos leads com colunas específicas (OTIMIZADA)
  const applyChannelFilterWithColumns = async (filter: string, columns: any[]) => {
    const startTime = performance.now();
    const pipelineForRows = pipelineNameRef.current || 'Funil de Vendas';
    console.log('🔄 Iniciando processamento de leads...', {
      total: allLeadsData.length,
      filter,
      pipeline: pipelineForRows,
    });

    let filteredLeads: any[] = [];

    const processBatch = (batch: any[], startIndex: number, batchSize: number = 1000): Promise<any[]> => {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          const endIndex = Math.min(startIndex + batchSize, batch.length);
          const batchResults: any[] = [];

          for (let i = startIndex; i < endIndex; i++) {
            const lead = batch[i];

            const leadPipeline = getLeadPipelineName(lead);
            if (leadPipeline !== pipelineForRows) {
              continue;
            }

            if (filter !== 'all') {
              const leadSource = (lead.contacts?.source || '').toLowerCase();
              const filterSource = filter.toLowerCase();
              if (leadSource !== filterSource) {
                continue;
              }
            }

            const normalizedSearch = (searchQuery || '').trim().toLowerCase();
            if (normalizedSearch) {
              const name = (lead.contacts?.name || '').toLowerCase();
              const email = (lead.contacts?.email || '').toLowerCase();
              const phone = (lead.contacts?.phone || '').toLowerCase();
              if (
                !name.includes(normalizedSearch) &&
                !email.includes(normalizedSearch) &&
                !phone.includes(normalizedSearch)
              ) {
                continue;
              }
            }

            batchResults.push(lead);
          }

          resolve(batchResults);
        });
      });
    };

    const batchSize = 1000;
    const batches: Promise<any[]>[] = [];

    for (let i = 0; i < allLeadsData.length; i += batchSize) {
      batches.push(processBatch(allLeadsData, i, batchSize));
    }

    const batchResults = await Promise.all(batches);
    filteredLeads = batchResults.flat();

    console.log(
      `✅ Leads filtrados: ${filteredLeads.length} de ${allLeadsData.length} (${(
        (performance.now() - startTime) /
        1000
      ).toFixed(2)}s)`
    );

    const leadsByStage = new Map<number | string, any[]>();

    for (const lead of filteredLeads) {
      const stageOrder = lead.crm_stages?.stage_order;
      if (stageOrder !== undefined && stageOrder !== null) {
        const normalizedOrder = typeof stageOrder === 'string' ? stageOrder : stageOrder.toString();
        const orderKey = normalizedOrder.includes('.')
          ? parseFloat(normalizedOrder)
          : parseInt(normalizedOrder, 10);

        if (!leadsByStage.has(orderKey)) {
          leadsByStage.set(orderKey, []);
        }
        leadsByStage.get(orderKey)!.push(lead);
      }
    }

    console.log('🔄 Mapping columns with provided columns, titles:', columns.map((col) => col.title));

    const updatedColumns = columns.map((column) => {
      const columnOrder = resolveKanbanColumnOrder(column as KanbanColumn);
      const stageLeads =
        columnOrder !== undefined && columnOrder !== null
          ? leadsByStage.get(columnOrder as number | string) || []
          : [];

      const columnLeads = stageLeads.map((lead: any) => ({
        id: lead.contacts?.id || lead.id,
        name: lead.contacts?.name || '',
        email: lead.contacts?.email || '',
        phone: lead.contacts?.phone || '',
        avatar: lead.contacts?.avatar_url || '',
        source: lead.contacts?.source || '',
        lastContact: lead.entered_at || lead.contacts?.created_at || new Date().toISOString(),
        value: lead.contacts?.custom_fields?.total_spent || 0,
        currency: lead.contacts?.custom_fields?.currency || 'BRL',
        notes: lead.notes || '',
        tags: Array.isArray(lead.contact_tags)
          ? lead.contact_tags.map((t: { id: string; name: string; color?: string | null }) => ({
              id: t.id,
              name: t.name,
              color: t.color ?? null,
            }))
          : [],
        crm_stages: lead.crm_stages,
        crm_pipelines: lead.crm_pipelines,
        custom_fields: lead.contacts?.custom_fields || lead.customFields || {},
        created_at: lead.contacts?.created_at || lead.entered_at,
      }));

      const realCount = columnLeads.length;
      console.log(`📊 Coluna ${column.id} (${column.title}): ${realCount} leads`);

      return {
        ...column,
        title: column.title,
        contacts: columnLeads,
        count: realCount,
      };
    });

    const totalTime = ((performance.now() - startTime) / 1000).toFixed(2);
    console.log(`🎯 Final columns after filter (${totalTime}s):`, updatedColumns.map((col) => col.title));
    setKanbanColumns(updatedColumns);
  };

  // Função para aplicar filtros aos leads (wrapper para compatibilidade)
  const applyChannelFilter = async (filter: string) => {
    await applyChannelFilterWithColumns(filter, kanbanColumns);
  };

  // Função para buscar configurações dos estágios
  const fetchStagesConfig = async () => {
    try {
      console.log('🔍 Fetching stages configuration from API...');
      const response = await axios.get(`${API_BASE_URL}/crm/stages-config`);
      console.log('📡 Stages config response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Stages config loaded:', response.data.data);
        console.log('📋 Stage names from config:', {
          stage1: response.data.data.stage1?.name,
          stage2: response.data.data.stage2?.name,
          stage3: response.data.data.stage3?.name,
          stage4: response.data.data.stage4?.name,
        });
        setStagesConfig(response.data.data);

        return response.data.data;
      } else {
        console.error('❌ API returned success=false:', response.data);
        return null;
      }
    } catch (err: any) {
      console.error('❌ Erro ao buscar configurações dos estágios:', err);
      console.error('Response data:', err.response?.data);
      return null;
    }
  };

  // Função para sincronizar estágios do banco com variáveis de ambiente
  const syncStagesFromEnv = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/crm/sync-stages-from-env`);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Estágios sincronizados com sucesso!',
          severity: 'success'
        });
        
        // Recarregar estágios e configurações
        const pipes = await fetchStages();
        await Promise.all([fetchAllStages(), fetchStagesConfig()]);
        const cur = pipelineNameRef.current;
        const pl = pipes?.find((x: any) => x.name === cur);
        const scoped = stagesScopedToPipeline(pl);
        if (scoped.length) {
          setKanbanColumns(buildKanbanColumnsFromStages(scoped));
        }
        setLeadsCache(null);
        await fetchLeads(undefined, undefined, { pipeline: cur });
      } else {
        throw new Error(response.data.error || 'Erro ao sincronizar estágios');
      }
    } catch (err: any) {
      console.error('Erro ao sincronizar estágios:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
      setSnackbar({
        open: true,
        message: `Erro ao sincronizar estágios: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar as colunas do Kanban com as configurações
  const updateKanbanColumnsWithConfig = (config: any) => {
    console.log('🔄 Updating Kanban columns with config:', config);
    
    const updatedColumns = [
      {
        id: 'new-lead',
        title: config.stage1?.name || 'Novo Lead',
        color: 'text.secondary',
        count: 0,
        contacts: [],
      },
      {
        id: 'grupo',
        title: config.stage2?.name || 'Grupo',
        color: '#3B82F6',
        count: 0,
        contacts: [],
      },
      {
        id: 'cadastro',
        title: config.stage3?.name || 'Cadastro',
        color: '#F59E0B',
        count: 0,
        contacts: [],
      },
      {
        id: 'ganho',
        title: config.stage4?.name || 'Ganho',
        color: '#10B981',
        count: 0,
        contacts: [],
      },
      {
        id: 'perdido',
        title: config.stage5?.name || 'Perdido',
        color: '#EF4444',
        count: 0,
        contacts: [],
      },
      {
        id: 'followup',
        title: config.stage6?.name || 'Follow-up',
        color: '#8B5CF6',
        count: 0,
        contacts: [],
      },
      {
        id: 'pre-negociacao',
        title: config.stage6_5?.name || 'Pré-Negociação',
        color: '#9C27B0',
        count: 0,
        contacts: [],
      },
      {
        id: 'negociacao',
        title: config.stage7?.name || 'Negociação',
        color: '#F97316',
        count: 0,
        contacts: [],
      },
      {
        id: 'fechamento',
        title: config.stage8?.name || 'Fechamento',
        color: '#06B6D4',
        count: 0,
        contacts: [],
      },
    ];
    
    // Adicionar estágios 9, 10 e 11 se estiverem configurados
    if (config.stage9?.name && config.stage9.name.trim() !== '') {
      updatedColumns.push({
        id: 'stage9',
        title: config.stage9.name,
        color: '#EC4899',
        count: 0,
        contacts: [],
      });
    }
    
    if (config.stage10?.name && config.stage10.name.trim() !== '') {
      updatedColumns.push({
        id: 'stage10',
        title: config.stage10.name,
        color: '#14B8A6',
        count: 0,
        contacts: [],
      });
    }
    
    if (config.stage11?.name && config.stage11.name.trim() !== '') {
      updatedColumns.push({
        id: 'stage11',
        title: config.stage11.name,
        color: '#F43F5E',
        count: 0,
        contacts: [],
      });
    }
    
    console.log('📋 Updated columns:', updatedColumns.map(col => ({ id: col.id, title: col.title })));
    console.log('🎯 FINAL COLUMN TITLES:', updatedColumns.map(col => col.title));
    setKanbanColumns(updatedColumns);
    console.log('✅ Kanban columns updated with config:', updatedColumns);
    
    // Se já temos leads, aplicar filtro com as novas colunas
    if (allLeadsData.length > 0) {
      setTimeout(() => {
        console.log('🔄 Applying filter with updated columns...');
        applyChannelFilterWithColumns(channelFilter, updatedColumns);
      }, 100);
    }
  };

  // Função para buscar estágios da API
  const fetchStages = async () => {
    try {
      console.log('🔍 Fetching stages from API...');
      const response = await axios.get(`${API_BASE_URL}/crm/pipelines`);
      console.log('📡 Raw pipelines response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Stages from API:', response.data.data);
        const data = response.data.data || [];
        setPipelines(data);
        return data;
      }
      console.error('❌ API returned success=false:', response.data);
      setPipelines([]);
      return [];
    } catch (err: any) {
      console.error('❌ Erro ao buscar estágios:', err);
      console.error('Response data:', err.response?.data);
      setPipelines([]);
      return [];
    }
  };

  // Função para buscar todos os estágios disponíveis
  const fetchAllStages = async () => {
    try {
      console.log('🔍 Fetching all stages from API...');
      const response = await axios.get(`${API_BASE_URL}/crm/stages`);
      console.log('📡 All stages response:', response.data);
      
      if (response.data.success) {
        console.log('✅ All stages loaded:', response.data.data);
        setAllStages(response.data.data || []);
        return response.data.data;
      } else {
        console.error('❌ API returned success=false:', response.data);
        setAllStages([]);
        return [];
      }
    } catch (err: any) {
      console.error('❌ Erro ao buscar todos os estágios:', err);
      console.error('Response data:', err.response?.data);
      setAllStages([]);
      return [];
    }
  };

  // Função para verificar se há scroll horizontal disponível
  const checkScrollAvailability = () => {
    if (kanbanContainerRef) {
      const hasHorizontalScroll = kanbanContainerRef.scrollWidth > kanbanContainerRef.clientWidth;
      setShowStageIndicator(hasHorizontalScroll);
    }
  };

  // Função para rolar para o próximo estágio
  const scrollToNextStage = () => {
    if (kanbanContainerRef) {
      const scrollAmount = kanbanContainerRef.clientWidth * 0.8; // 80% da largura visível
      kanbanContainerRef.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Função para rolar para o estágio anterior
  const scrollToPreviousStage = () => {
    if (kanbanContainerRef) {
      const scrollAmount = kanbanContainerRef.clientWidth * 0.8; // 80% da largura visível
      kanbanContainerRef.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Função para mostrar todos os estágios
  const showAllStages = () => {
    if (kanbanContainerRef) {
      kanbanContainerRef.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
    }
  };

  // Função para mover lead para próximo estágio
  const moveToNextStage = useCallback(async (contactId: string) => {
    try {
      await axios.post(`${API_BASE_URL}/crm/contact/${contactId}/move-stage`);
      await fetchLeads(); // Recarregar dados
    } catch (err) {
      console.error('Erro ao mover lead:', err);
      setError('Erro ao mover lead para próximo estágio');
    }
  }, []);

  // Função para mover lead para um estágio específico
  const moveContactToStage = async (contactId: string, targetColumnId: string) => {
    const normOrd = (o: unknown) => parseFloat(String(o));

    try {
      console.log('🔄 Starting moveContactToStage:', { contactId, targetColumnId });

      const reloadAfterMove = async () => {
        await fetchLeads(searchQuery, { ...advancedFilters, source: channelFilter });
      };

      if (CRM_STAGE_UUID_RE.test(targetColumnId)) {
        const response = await axios.post(`${API_BASE_URL}/crm/contact/${contactId}/move-to-stage`, {
          stageId: targetColumnId,
        });
        if (response.data.success) {
          setSnackbar({
            open: true,
            message: 'Lead movido com sucesso!',
            severity: 'success',
          });
          await reloadAfterMove();
          return;
        }
        throw new Error(response.data.error || 'Falha ao mover contato');
      }

      const stagesData = await fetchStages();
      if (!stagesData?.length) {
        throw new Error('Falha ao buscar estágios');
      }

      console.log('📊 Stages data:', stagesData);
      console.log('📊 Number of pipelines:', stagesData.length);
      console.log('📊 Pipeline names:', stagesData.map((p: any) => p.name));
      console.log(
        '📊 Available stage orders:',
        stagesData.flatMap((p: any) =>
          stagesScopedToPipeline(p).map((s: any) => ({ pipeline: p.name, stage: s.name, order: s.stage_order }))
        )
      );
      
      // Encontrar o estágio correto baseado na ordem
      const columnToOrder: { [key: string]: number | string } = {
        'new-lead': 1,
        'grupo': 2,
        'cadastro': 3,
        'ganho': 4,
        'perdido': 5,
        'followup': 6,
        'pre-negociacao': 6.5,
        'negociacao': 7,
        'fechamento': 8,
        'stage9': 9,
        'stage10': 10,
        'stage11': 11
      };
      
      const targetOrder = columnToOrder[targetColumnId as keyof typeof columnToOrder];
      if (!targetOrder) {
        throw new Error(`Coluna de destino inválida: ${targetColumnId}`);
      }
      
      console.log('🎯 Target order:', targetOrder);
      
      // Primeiro, encontrar o lead atual para saber qual pipeline ele pertence
      const currentLead = allLeadsData.find((lead: any) => lead.contacts?.id === contactId);
      if (!currentLead) {
        throw new Error('Lead não encontrado nos dados atuais');
      }
      
      const currentPipelineName = currentLead.crm_pipelines?.name;
      const currentStageId = currentLead.crm_stages?.id;
      console.log('📍 Current lead pipeline:', currentPipelineName || 'Sem pipeline definido');
      console.log('📍 Current lead stage ID:', currentStageId);
      
      // Buscar o estágio APENAS no mesmo pipeline do lead atual
      let targetStage = null;
      let actualPipelineName = currentPipelineName;
      
      // Se o pipeline não está definido, descobrir pelo estágio atual
      if (!currentPipelineName && currentStageId) {
        console.log('🔍 Pipeline not defined, finding by current stage...');
        for (const pipeline of stagesData) {
          const scoped = stagesScopedToPipeline(pipeline);
          if (scoped.some((stage: any) => stage.id === currentStageId)) {
            actualPipelineName = pipeline.name;
            console.log(`🎯 Found actual pipeline by stage: ${actualPipelineName}`);
            break;
          }
        }
      }
      
      if (actualPipelineName) {
        const currentPipeline = stagesData.find((p: any) => p.name === actualPipelineName);
        const scopedCp = stagesScopedToPipeline(currentPipeline);
        console.log('🔍 Current pipeline found:', currentPipeline);
        if (scopedCp.length) {
          console.log(
            '🔍 Available stages in current pipeline:',
            scopedCp.map((s: any) => ({ id: s.id, name: s.name, order: s.stage_order }))
          );
          targetStage = scopedCp.find((stage: any) => normOrd(stage.stage_order) === normOrd(targetOrder));
          if (targetStage) {
            console.log(`✅ Found stage in current pipeline: ${actualPipelineName}`, targetStage);
          } else {
            console.log(`❌ Stage with order ${targetOrder} not found in current pipeline`);
          }
        }
      } else {
        // Se ainda não conseguiu identificar, usar o primeiro pipeline que tiver o estágio
        console.log('⚠️ Could not identify pipeline, using fallback...');
        for (const pipeline of stagesData) {
          const scoped = stagesScopedToPipeline(pipeline);
          if (scoped.length) {
            console.log(
              `🔍 Checking pipeline ${pipeline.name}:`,
              scoped.map((s: any) => ({ id: s.id, name: s.name, order: s.stage_order }))
            );
            targetStage = scoped.find((stage: any) => normOrd(stage.stage_order) === normOrd(targetOrder));
            if (targetStage) {
              console.log(`✅ Found stage in pipeline (fallback): ${pipeline.name}`, targetStage);
              actualPipelineName = pipeline.name;
              break;
            }
          }
        }
      }

      if (!targetStage) {
        console.error('❌ Target stage not found for order:', targetOrder, 'in pipeline:', actualPipelineName);
        console.log(
          'Available stages in current pipeline:',
          actualPipelineName
            ? stagesScopedToPipeline(stagesData.find((p: any) => p.name === actualPipelineName))
            : stagesData.flatMap((p: any) => stagesScopedToPipeline(p))
        );
        throw new Error(`Estágio com ordem ${targetOrder} não encontrado no pipeline ${actualPipelineName || 'atual'}`);
      }
      
      console.log('✅ Moving contact to stage:', { id: targetStage.id, name: targetStage.name, order: targetStage.stage_order });
      console.log('📡 Sending request to API:', {
        url: `${API_BASE_URL}/crm/contact/${contactId}/move-to-stage`,
        stageId: targetStage.id,
        stageIdType: typeof targetStage.id
      });
      
      // Verificar se o stageId é um UUID válido
      if (!targetStage.id || typeof targetStage.id !== 'string' || targetStage.id.length < 10) {
        throw new Error(`Stage ID inválido: ${targetStage.id}. Esperado UUID, recebido: ${typeof targetStage.id}`);
      }
      
      // Fazer a requisição para mover o contato
      const response = await axios.post(`${API_BASE_URL}/crm/contact/${contactId}/move-to-stage`, {
        stageId: targetStage.id
      });
      
      console.log('📡 API Response:', response.data);
      
      if (response.data.success) {
        console.log('🎉 Contact moved successfully');
        setSnackbar({
          open: true,
          message: 'Lead movido com sucesso!',
          severity: 'success',
        });
        await reloadAfterMove();
      } else {
        throw new Error(response.data.error || 'Falha ao mover contato');
      }
    } catch (err: any) {
      console.error('❌ Erro ao mover lead:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
      setSnackbar({
        open: true,
        message: `Erro ao mover lead: ${errorMessage}`,
        severity: 'error'
      });
      setError(`Erro ao mover lead: ${errorMessage}`);
      throw err;
    }
  };

  // Funções para drag and drop - Otimizado para máxima fluidez
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    
    // Usar cache para encontrar contato rapidamente - sem cálculos pesados
    const contact = contactsMap.get(active.id as string);
    setDraggedContact(contact || null);
    
    // Desabilitar scroll durante drag para melhor performance
    document.body.style.overflow = 'hidden';
    document.body.style.userSelect = 'none'; // Prevenir seleção de texto durante drag
  }, [contactsMap]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    // Apenas para feedback visual - sem cálculos pesados
    // O dnd-kit já gerencia o estado visual automaticamente
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    // Reabilitar scroll e seleção
    document.body.style.overflow = '';
    document.body.style.userSelect = '';
    
    setActiveId(null);
    setDraggedContact(null);

    // TODOS OS CÁLCULOS APENAS AQUI - após soltar
    if (!over) {
      return;
    }

    if (active.id === over.id) {
      return;
    }

    const contactId = active.id as string;
    // Verificar se o over é uma coluna ou um contato dentro de uma coluna
    let targetColumnId = over.id as string;
    
    // Se o over for um contato, encontrar a coluna pai
    if (over.data?.current?.type === 'contact') {
      const targetContact = over.data.current.contact;
      const parentColumn = kanbanColumns.find(col => 
        col.contacts.some(c => c.id === targetContact.id)
      );
      if (parentColumn) {
        targetColumnId = parentColumn.id;
      }
    }

    // Verificar se o over é uma coluna válida
    const targetColumn = kanbanColumns.find(col => col.id === targetColumnId);
    if (!targetColumn) {
      return;
    }

    // Verificar se o contato está sendo movido para uma coluna diferente
    const currentColumn = kanbanColumns.find(col => 
      col.contacts.some(contact => contact.id === contactId)
    );
    
    if (!currentColumn) {
      return;
    }
    
    if (currentColumn.id === targetColumnId) {
      return;
    }

    // Apenas agora fazer a chamada à API
    try {
      setIsMovingContact(true);
      await moveContactToStage(contactId, targetColumnId);
    } catch (error) {
      console.error('Error moving contact:', error);
      setError('Erro ao mover lead para o novo estágio');
    } finally {
      setIsMovingContact(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const pipes = await fetchStages();
        await Promise.all([fetchStagesConfig(), fetchAllStages()]);
        const list = pipes || [];
        let savedName: string | null = null;
        try {
          savedName = sessionStorage.getItem(CRM_SELECTED_PIPELINE_KEY);
        } catch {
          savedName = null;
        }
        const defaultName =
          (savedName && list.some((p: any) => p.name === savedName) ? savedName : null) ||
          list.find((p: any) => p.name === 'Funil de Vendas')?.name ||
          list[0]?.name ||
          'Funil de Vendas';
        pipelineNameRef.current = defaultName;
        setSelectedPipelineName(defaultName);
        const p = list.find((x: any) => x.name === defaultName);
        const bootStages = stagesScopedToPipeline(p);
        if (bootStages.length) {
          setKanbanColumns(buildKanbanColumnsFromStages(bootStages));
        }
        setLeadsCache(null);
        await fetchLeads(undefined, undefined, { pipeline: defaultName });
      } catch (e) {
        console.error('CRM bootstrap pipelines:', e);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (allLeadsData.length === 0 || !selectedPipelineName) return;
    const pipe = pipelines.find((x: any) => x.name === selectedPipelineName);
    const scoped = stagesScopedToPipeline(pipe);
    if (!scoped.length) return;
    const baseColumns = buildKanbanColumnsFromStages(scoped);
    void applyChannelFilterWithColumns(channelFilter, baseColumns);
  }, [channelFilter, allLeadsData, selectedPipelineName, pipelines, searchQuery]);

  // Definir o estágio inicial quando um contato for selecionado
  useEffect(() => {
    if (selectedContact?.crm_stages?.id) {
      setSelectedStageId(selectedContact.crm_stages.id);
    }
  }, [selectedContact]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/contact-tags`);
        if (cancelled || !res.data?.success) return;
        setCrmCatalogTags(res.data.data || []);
      } catch {
        if (!cancelled) setCrmCatalogTags([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Busca estatísticas por estágio no período selecionado
  const fetchStageStats = async () => {
    try {
      const params = getPeriodParams(period, dateFrom, dateTo);
      
      // ✅ Adiciona filtro de canal se não for 'all'
      if (channelFilter && channelFilter !== 'all') {
        params.source = channelFilter;
      }
      
      // Tentar cache primeiro
      let data;
      try {
        console.log('🔍 CRM Stats (Kanban): Tentando buscar do cache...', params);
        const cacheResponse = await Promise.race([
          axios.get(`${API_BASE_URL}/crm/cache/stats`, { params, timeout: 10000 }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000))
        ]) as any;
        data = cacheResponse.data;
        console.log('📦 CRM Stats (Kanban): Dados obtidos do cache', data.message);
      } catch (cacheError: any) {
        // Se cache falhar, usar endpoint normal
        console.log('🔄 CRM Stats (Kanban): Cache não disponível ou timeout, usando endpoint normal', cacheError?.message || cacheError);
        try {
          const normalResponse = await axios.get(`${API_BASE_URL}/crm/stats`, { params, timeout: 25000 });
          data = normalResponse.data;
          console.log('✅ CRM Stats (Kanban): Dados obtidos do endpoint normal');
        } catch (normalError: any) {
          console.error('❌ CRM Stats (Kanban): Erro em ambos os endpoints:', normalError?.message || normalError);
          return;
        }
      }
      
      if (!data?.success) return;

      const stageStats: Array<{ name: string; stage_order: number; count: number; color?: string }>
        = (data.data?.stageStats || []) as any;

      // Mapa de stage_order -> coluna do Kanban
      const orderToColumnId: Record<number | string, string> = {
        1: 'new-lead',
        2: 'grupo',
        3: 'cadastro',
        4: 'ganho',
        5: 'perdido',
        6: 'followup',
        6.5: 'pre-negociacao',
        7: 'negociacao',
        8: 'fechamento',
        9: 'stage9',
        10: 'stage10',
        11: 'stage11'
      };

      // Atualizar apenas os counts, preservando os contatos listados
      setKanbanColumns((prev) => {
        if (prev.some((col) => col.stageOrder !== undefined && col.stageOrder !== '')) {
          return prev;
        }

        const countsByColumnLegacy: Record<string, number> = {};
        stageStats.forEach((s) => {
          const statOrder = typeof s.stage_order === 'string' ? parseFloat(s.stage_order) : s.stage_order;
          const colId = orderToColumnId[statOrder];
          if (!colId) {
            console.debug(`⚠️ Stage order ${statOrder} não mapeado para coluna`);
            return;
          }
          countsByColumnLegacy[colId] = (countsByColumnLegacy[colId] || 0) + (Number(s.count) || 0);
        });

        return prev.map((col) => ({
          ...col,
          count: countsByColumnLegacy[col.id] ?? 0,
        }));
      });
    } catch (e) {
      console.error('Erro ao buscar estatísticas por período:', e);
    }
  };

  // Atualizar ao mudar o período
  useEffect(() => {
    // Ignorar se for período custom (será aplicado via applyCustomDates)
    if (period === 'custom') {
      return;
    }
    
    const applyPeriodFilter = async () => {
      if (period === 'today') {
        applyTodayFilter();
      } else if (period === 'yesterday') {
        applyYesterdayFilter();
      } else {
        // Para outros períodos pré-definidos, buscar estatísticas
        await fetchStageStats();
      }
      
      // Buscar leads novamente com o novo período
      await fetchLeads();
      // O useEffect que monitora allLeadsData.length aplicará o filtro automaticamente
    };
    
    applyPeriodFilter();
  }, [period, dateFrom, dateTo]); // ✅ Reage a mudanças no período e datas (exceto custom)

  // Aplicar datas personalizadas
  const applyCustomDates = async () => {
    if (period !== 'custom') return;
    
    // Validar se ambas as datas foram selecionadas
    if (!dateFrom || !dateTo) {
      setSnackbar({
        open: true,
        message: 'Por favor, selecione ambas as datas (De e Até)',
        severity: 'warning'
      });
      return;
    }
    
    // Aplicar filtro de datas personalizadas
    await fetchStageStats();
    await fetchLeads(); // Recarregar leads com o novo período
    // O useEffect que monitora allLeadsData.length aplicará o filtro automaticamente
  };

  // Aplicar filtro de hoje
  const applyTodayFilter = () => {
    if (period !== 'today') return;
    fetchStageStats();
  };

  // Aplicar filtro de ontem
  const applyYesterdayFilter = () => {
    if (period !== 'yesterday') return;
    fetchStageStats();
  };

  // Inicializar counts do período na montagem
  useEffect(() => {
    fetchStageStats();
  }, []);

  // Verificar disponibilidade de scroll quando as colunas mudam
  useEffect(() => {
    if (kanbanColumns.length > 0) {
      setTimeout(() => {
        checkScrollAvailability();
      }, 100);
    }
  }, [kanbanColumns]);

  // Verificar disponibilidade de scroll quando a janela redimensiona
  useEffect(() => {
    const handleResize = () => {
      checkScrollAvailability();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [kanbanContainerRef]);

  const tagIdsFilterKey = advancedFilters.tagIds.join(',');

  useEffect(() => {
    if (pipelines.length === 0) return;
    void fetchLeads(searchQuery, { ...advancedFilters, source: channelFilter });
  }, [tagIdsFilterKey, pipelines.length]);

  // Sincronizar scroll entre a barra superior, cards de métricas e kanban
  useEffect(() => {
    if (!scrollBarRef) return;

    const handleScrollBarScroll = () => {
      if (scrollBarRef) {
        const scrollLeft = scrollBarRef.scrollLeft;
        // Sincronizar com todas as barras de scroll
        const scrollBars = document.querySelectorAll('[data-scroll-sync]');
        scrollBars.forEach((bar: any) => {
          if (bar !== scrollBarRef) {
            bar.scrollLeft = scrollLeft;
          }
        });
      }
    };

    scrollBarRef.addEventListener('scroll', handleScrollBarScroll);

    return () => {
      scrollBarRef.removeEventListener('scroll', handleScrollBarScroll);
    };
  }, [scrollBarRef]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleContactClick = useCallback((contact: any) => {
    setSelectedContact(contact);
    setOpenLeadDetailsDialog(true);
  }, []);

  const handleCloseLeadDetails = () => {
    setOpenLeadDetailsDialog(false);
    setSelectedContact(null);
    setSelectedStageId('');
  };

  // Função para alterar o estágio do lead
  const handleStageChange = async (newStageId: string) => {
    if (!selectedContact || !newStageId) return;
    
    try {
      console.log('🔄 Changing stage for contact:', selectedContact.id, 'to stage:', newStageId);
      
      const response = await axios.post(`${API_BASE_URL}/crm/contact/${selectedContact.id}/move-to-stage`, {
        stageId: newStageId
      });
      
      if (response.data.success) {
        console.log('✅ Stage changed successfully');
        setSnackbar({
          open: true,
          message: 'Estágio alterado com sucesso!',
          severity: 'success'
        });
        
        // Atualizar o selectedContact com o novo estágio
        const stagesData = await fetchStages();
        if (stagesData?.length) {
          let newStage = null;
          for (const pipeline of stagesData) {
            const scoped = stagesScopedToPipeline(pipeline);
            newStage = scoped.find((stage: any) => stage.id === newStageId) || null;
            if (newStage) break;
          }

          if (newStage) {
            setSelectedContact({
              ...selectedContact,
              crm_stages: newStage,
            });
          }
        }

        await fetchLeads(searchQuery, { ...advancedFilters, source: channelFilter });
      } else {
        throw new Error(response.data.error || 'Falha ao alterar estágio');
      }
    } catch (err: any) {
      console.error('❌ Erro ao alterar estágio:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      const errorMessage = err.response?.data?.error || err.message || 'Erro desconhecido';
      setSnackbar({
        open: true,
        message: `Erro ao alterar estágio: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  const handleOpenEditPipelineStages = () => {
    const p = pipelines.find((x: any) => x.name === selectedPipelineName);
    const sorted = sortStagesForPicker([...stagesScopedToPipeline(p)]);
    if (!sorted.length) {
      setSnackbar({
        open: true,
        message: 'Selecione uma pipeline com estágios.',
        severity: 'warning',
      });
      return;
    }
    setPipelineStagesEditRows(
      sorted.map((s: any) => ({ id: s.id, stage_order: s.stage_order }))
    );
    const drafts: Record<string, string> = {};
    sorted.forEach((s: any) => {
      drafts[s.id] = s.name ?? '';
    });
    setPipelineStageNameDrafts(drafts);
    setOpenEditPipelineStagesDialog(true);
  };

  const handleSaveEditPipelineStages = async () => {
    const p = pipelines.find((x: any) => x.name === selectedPipelineName);
    const scopedStages = stagesScopedToPipeline(p);
    if (!scopedStages.length) return;
    const byId = new Map<string, any>(scopedStages.map((s: any) => [s.id, s]));
    for (const row of pipelineStagesEditRows) {
      const name = (pipelineStageNameDrafts[row.id] ?? '').trim();
      if (!name) {
        setSnackbar({
          open: true,
          message: 'Todos os estágios precisam de um nome.',
          severity: 'warning',
        });
        return;
      }
    }
    setSavingPipelineStageNames(true);
    try {
      const promises: Promise<unknown>[] = [];
      for (const row of pipelineStagesEditRows) {
        const name = pipelineStageNameDrafts[row.id].trim();
        const orig = byId.get(row.id);
        if (orig && name !== orig.name) {
          const q = p?.id ? `?pipeline_id=${encodeURIComponent(p.id)}` : '';
          promises.push(axios.put(`${API_BASE_URL}/crm/stages/${row.id}${q}`, { name }));
        }
      }
      await Promise.all(promises);
      setSnackbar({
        open: true,
        message:
          promises.length > 0 ? 'Nomes dos estágios atualizados.' : 'Nenhuma alteração a salvar.',
        severity: 'success',
      });
      setOpenEditPipelineStagesDialog(false);
      const pipes = await fetchStages();
      await fetchAllStages();
      const cur = pipelineNameRef.current || selectedPipelineName;
      const pl = pipes.find((x: any) => x.name === cur);
      const afterSave = stagesScopedToPipeline(pl);
      if (afterSave.length) {
        setKanbanColumns(buildKanbanColumnsFromStages(afterSave));
      }
      setLeadsCache(null);
      await fetchLeads(searchQuery, { ...advancedFilters, source: channelFilter }, { pipeline: cur });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message || 'Erro ao salvar nomes dos estágios',
        severity: 'error',
      });
    } finally {
      setSavingPipelineStageNames(false);
    }
  };

  const handleAddContact = () => {
    const pn =
      selectedPipelineName ||
      pipelineNameRef.current ||
      pipelines.find((p: any) => p.name === 'Funil de Vendas')?.name ||
      pipelines[0]?.name ||
      '';
    const pipe = pipelines.find((p: any) => p.name === pn);
    const addScoped = stagesScopedToPipeline(pipe);
    const sorted = addScoped.length ? sortStagesForPicker(addScoped) : [];
    const firstStageId = sorted[0]?.id || '';
    setContactForm((prev) => ({
      ...prev,
      pipelineName: pn,
      stage: firstStageId,
    }));
    setOpenContactDialog(true);
  };

  const handleSaveContact = async () => {
    try {
      if (!contactForm.name.trim()) {
        alert('Nome é obrigatório');
        return;
      }

      let stageId: string | null = null;
      const pipeline = pipelines.find((p: any) => p.name === contactForm.pipelineName);
      const pipelineStages = stagesScopedToPipeline(pipeline);

      try {
        if (contactForm.stage && CRM_STAGE_UUID_RE.test(contactForm.stage)) {
          const match = pipelineStages.find((s: any) => s.id === contactForm.stage);
          if (match) {
            stageId = match.id;
          }
        } else if (pipelineStages.length && contactForm.stage) {
          const match = pipelineStages.find(
            (s: any) => String(s.stage_order) === String(getStageOrderFromColumnId(contactForm.stage))
          );
          if (match) {
            stageId = match.id;
          }
        }
        if (!stageId && pipelineStages.length) {
          const sorted = sortStagesForPicker(pipelineStages);
          stageId = sorted[0]?.id ?? null;
        }
      } catch (error) {
        console.error('Erro ao buscar estágios:', error);
      }

      const contactData: any = {
        name: contactForm.name,
        email: contactForm.email || null,
        phone: contactForm.phone || null,
        source: 'crm',
        notes: contactForm.notes || null,
        custom_fields: contactForm.value ? { estimated_value: parseFloat(contactForm.value) || 0 } : {},
      };

      if (stageId) {
        contactData.stage_id = stageId;
      }

      if (Array.isArray(contactForm.tagIds) && contactForm.tagIds.length > 0) {
        contactData.tag_ids = contactForm.tagIds;
      }

      console.log('Criando contato:', contactData);

      const response = await axios.post(`${API_BASE_URL}/crm/contacts`, contactData);

      if (response.data.success) {
        console.log('Contato criado com sucesso:', response.data.data);
        const savedPipeline = contactForm.pipelineName;

        setOpenContactDialog(false);
        setContactForm({
          name: '',
          email: '',
          phone: '',
          value: '',
          notes: '',
          pipelineName: '',
          stage: '',
          tagIds: [],
        });

        await fetchLeads(searchQuery, { ...advancedFilters, source: channelFilter });

        setSnackbar({
          open: true,
          message: savedPipeline
            ? `Lead criado na pipeline "${savedPipeline}".`
            : 'Lead criado com sucesso!',
          severity: 'success',
        });
      } else {
        throw new Error(response.data.error || 'Erro ao criar lead');
      }
    } catch (error: any) {
      console.error('Erro ao criar contato:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Erro ao criar lead. Tente novamente.',
        severity: 'error',
      });
    }
  };

  const getStageOrderFromColumnId = (columnId: string): number => {
    if (CRM_STAGE_UUID_RE.test(columnId)) {
      const pipe = pipelines.find((p: any) => p.name === pipelineNameRef.current);
      const scoped = stagesScopedToPipeline(pipe);
      const st = scoped.find((s: any) => s.id === columnId);
      if (st != null && st.stage_order !== undefined && st.stage_order !== null) {
        return parseFloat(String(st.stage_order));
      }
      return 1;
    }
    const columnOrderMap: { [key: string]: number | string } = {
      'new-lead': 1,
      grupo: 2,
      cadastro: 3,
      ganho: 4,
      perdido: 5,
      followup: 6,
      'pre-negociacao': 6.5,
      negociacao: 7,
      fechamento: 8,
      stage9: 9,
      stage10: 10,
      stage11: 11,
    };
    const order = columnOrderMap[columnId];
    return typeof order === 'number' ? order : typeof order === 'string' ? parseFloat(order) : 1;
  };

  const handlePipelineSelect = async (name: string) => {
    pipelineNameRef.current = name;
    setSelectedPipelineName(name);
    try {
      sessionStorage.setItem(CRM_SELECTED_PIPELINE_KEY, name);
    } catch {
      /* ignore */
    }
    setAllLeadsData([]);
    setLeadsCache(null);
    const p = pipelines.find((x: any) => x.name === name);
    const scoped = stagesScopedToPipeline(p);
    if (scoped.length) {
      setKanbanColumns(buildKanbanColumnsFromStages(scoped));
    }
    await fetchLeads(searchQuery, { ...advancedFilters, source: channelFilter }, { pipeline: name });
  };

  const handleOpenNewPipelineDialog = () => {
    setNewPipelineName('');
    setNewPipelineDescription('');
    setNewPipelineColor('#3B82F6');
    setOpenNewPipelineDialog(true);
  };

  const handleCreatePipeline = async () => {
    const trimmed = newPipelineName.trim();
    if (!trimmed) {
      setSnackbar({ open: true, message: 'Informe o nome da pipeline.', severity: 'warning' });
      return;
    }
    try {
      setCreatingPipeline(true);
      const hex = newPipelineColor.trim();
      const response = await axios.post(`${API_BASE_URL}/crm/pipelines`, {
        name: trimmed,
        description: newPipelineDescription.trim() || undefined,
        color: /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : undefined,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Falha ao criar pipeline');
      }
      setSnackbar({
        open: true,
        message: 'Pipeline criada com estágios padrão.',
        severity: 'success',
      });
      setOpenNewPipelineDialog(false);
      const pipes = await fetchStages();
      pipelineNameRef.current = trimmed;
      setSelectedPipelineName(trimmed);
      try {
        sessionStorage.setItem(CRM_SELECTED_PIPELINE_KEY, trimmed);
      } catch {
        /* ignore */
      }
      setAllLeadsData([]);
      const created = pipes?.find((x: any) => x.name === trimmed);
      const createdStages = stagesScopedToPipeline(created);
      if (createdStages.length) {
        setKanbanColumns(buildKanbanColumnsFromStages(createdStages));
      }
      setLeadsCache(null);
      await fetchLeads(searchQuery, { ...advancedFilters, source: channelFilter }, { pipeline: trimmed });
    } catch (err: any) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message || 'Erro ao criar pipeline',
        severity: 'error',
      });
    } finally {
      setCreatingPipeline(false);
    }
  };

  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };

  const handleChannelFilterChange = async (filter: string) => {
    setChannelFilter(filter);
    handleFilterMenuClose();
    
    // Buscar leads com filtros atualizados
    await fetchLeads(searchQuery, { ...advancedFilters, source: filter });
    // O useEffect que monitora allLeadsData.length e channelFilter aplicará o filtro automaticamente
  };

  // Função para busca com debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    
    // Limpar timeout anterior
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Criar novo timeout para debounce (500ms)
    const timeout = setTimeout(() => {
      fetchLeads(value, { ...advancedFilters, source: channelFilter });
    }, 500);
    
    setSearchTimeout(timeout);
  };

  // Função para aplicar filtros avançados
  const handleAdvancedFiltersChange = (newFilters: any) => {
    setAdvancedFilters(newFilters);
    fetchLeads(searchQuery, { ...newFilters, source: channelFilter });
  };

  const formatCurrency = (value: number, currency: string = 'BRL') => {
    const locale = currency === 'USD' ? 'en-US' : 'pt-BR';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'transparent', minHeight: '100vh', pt: 0, px: 3, pb: 3, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        {/* Esquerda: Título e Descrição */}
        <Box>
          <Typography variant="h4" component="h1" sx={{ 
            fontWeight: 'bold',
            color: '#000000',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            mb: 0.5
          }}>
            CRM - Controle de Conversas e Pacientes
          </Typography>
          <Typography variant="body2" sx={{ 
            color: '#64748b',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Gerencie suas conversas e pacientes de todos os canais (WhatsApp, Telegram, Instagram, WebChat)
          </Typography>
        </Box>

        {/* Centro: Filtros e Busca */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexWrap: 'wrap',
          flex: 1,
          justifyContent: 'center'
        }}>
          <TextField
            size="small"
            placeholder="Buscar lead por nome, email ou telefone"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{
              minWidth: 250,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(139, 92, 246, 0.05)',
                '& fieldset': {
                  borderColor: 'rgba(139, 92, 246, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(139, 92, 246, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(139, 92, 246, 0.8)',
                },
              },
              '& .MuiInputBase-input': {
                color: '#475569',
              },
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#64748b' }} />,
            }}
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterMenuOpen}
            sx={{
              backgroundColor: channelFilter !== 'all' 
                ? 'rgba(76, 175, 80, 0.2)' 
                : 'transparent',
              color: channelFilter !== 'all' 
                ? '#81c784' 
                : '#64748b',
              borderColor: channelFilter !== 'all' 
                ? 'rgba(76, 175, 80, 0.3)' 
                : 'rgba(139, 92, 246, 0.2)',
              '&:hover': {
                borderColor: 'rgba(139, 92, 246, 0.5)',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
              },
            }}
          >
            Filtrar
            {channelFilter !== 'all' && (
              <Chip 
                label={channelFilter.charAt(0).toUpperCase() + channelFilter.slice(1)} 
                size="small" 
                sx={{ 
                  ml: 1, 
                  height: 20,
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: '#81c784',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                }} 
              />
            )}
          </Button>

          {/* Seletor de Período */}
          <PeriodFilter
            period={period}
            onPeriodChange={setPeriod}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onApply={applyCustomDates}
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="crm-pipeline-select-label">Pipeline</InputLabel>
            <Select
              labelId="crm-pipeline-select-label"
              label="Pipeline"
              value={pipelines.some((p: any) => p.name === selectedPipelineName) ? selectedPipelineName : ''}
              onChange={(e) => void handlePipelineSelect(String(e.target.value))}
            >
              {pipelines.map((p: any) => (
                <MenuItem key={p.id} value={p.name}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 220, maxWidth: 340 }}
            options={crmCatalogTags}
            getOptionLabel={(o) => o.name}
            value={crmCatalogTags.filter((t) => advancedFilters.tagIds.includes(t.id))}
            onChange={(_, v) =>
              setAdvancedFilters({
                ...advancedFilters,
                tagIds: v.map((x) => x.id),
              })
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  size="small"
                  sx={{
                    borderLeft: '3px solid',
                    borderColor: option.color || '#94a3b8',
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Tags" placeholder="Filtrar por tag (qualquer)" />
            )}
          />

          <Tooltip title="Editar nomes dos estágios desta pipeline">
            <span>
              <IconButton
                onClick={handleOpenEditPipelineStages}
                disabled={!selectedPipelineName || !pipelines.length}
                color="primary"
                aria-label="Editar estágios da pipeline"
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Button variant="outlined" size="small" onClick={handleOpenNewPipelineDialog}>
            Nova pipeline
          </Button>
        </Box>

        {/* Direita: Ações */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={syncStagesFromEnv}
            disabled={loading}
            sx={{
              color: '#475569',
              borderColor: '#94a3b8',
              '&:hover': {
                borderColor: 'rgba(139, 92, 246, 0.5)',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
              },
              '&.Mui-disabled': {
                color: '#94a3b8',
                borderColor: 'rgba(139, 92, 246, 0.1)',
              },
            }}
          >
            Sincronizar Estágios
          </Button>
          
          <Tooltip title="Atualizar">
            <IconButton
              onClick={() => fetchLeads(searchQuery, advancedFilters)}
              disabled={loading}
              className="text-purple-600 dark:text-purple-400 hover:bg-purple-500/10 dark:hover:bg-purple-500/20"
            >
              <SyncIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddContact}
            sx={{
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              color: '#ffffff',
              boxShadow: '0 4px 14px rgba(20, 184, 166, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0d9488 0%, #047857 100%)',
                boxShadow: '0 6px 20px rgba(20, 184, 166, 0.5)',
              },
            }}
          >
            Novo Lead
          </Button>

          <IconButton 
            onClick={handleMenuOpen}
            sx={{
              color: '#8b5cf6',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
              },
            }}
          >
            <MoreVertIcon />
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(139, 92, 246, 0.08) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(139, 92, 246, 0.18)',
                borderRadius: '8px',
                mt: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(139, 92, 246, 0.05)',
              }
            }}
          >
            <MenuItem 
              onClick={handleMenuClose}
              sx={{
                color: '#475569',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              Exportar
            </MenuItem>
            <MenuItem 
              onClick={handleMenuClose}
              sx={{
                color: '#475569',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              Importar
            </MenuItem>
            <MenuItem 
              onClick={handleMenuClose}
              sx={{
                color: '#475569',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              Configurações
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Menu de Filtros */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(139, 92, 246, 0.08) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(139, 92, 246, 0.18)',
            borderRadius: '8px',
            mt: 1,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(139, 92, 246, 0.05)',
          }
        }}
      >
        <MenuItem 
          onClick={() => handleChannelFilterChange('all')}
          selected={channelFilter === 'all'}
          sx={{
            color: '#475569',
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(139, 92, 246, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.3)',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#475569' }}>Todos os Canais</Typography>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => handleChannelFilterChange('whatsapp')}
          selected={channelFilter === 'whatsapp'}
          sx={{
            color: '#475569',
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(139, 92, 246, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.3)',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
            <Typography sx={{ color: '#475569' }}>WhatsApp</Typography>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => handleChannelFilterChange('telegram')}
          selected={channelFilter === 'telegram'}
          sx={{
            color: '#475569',
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(139, 92, 246, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.3)',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TelegramIcon sx={{ fontSize: 18, color: '#229ED9' }} />
            <Typography sx={{ color: '#475569' }}>Telegram</Typography>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => handleChannelFilterChange('instagram')}
          selected={channelFilter === 'instagram'}
          sx={{
            color: '#475569',
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(139, 92, 246, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.3)',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InstagramIcon sx={{ fontSize: 18, color: '#E4405F' }} />
            <Typography sx={{ color: '#475569' }}>Instagram</Typography>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={() => handleChannelFilterChange('webchat')}
          selected={channelFilter === 'webchat'}
          sx={{
            color: '#475569',
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(139, 92, 246, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.3)',
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WebIcon sx={{ fontSize: 18, color: '#6366F1' }} />
            <Typography sx={{ color: '#475569' }}>WebChat</Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Filtros Avançados */}
      {showAdvancedFilters && (
        <Card className="glass-blue-card" sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">Filtros Avançados</Typography>
            <Button
              size="small"
              onClick={() => {
                setAdvancedFilters({
                  source: 'all',
                  hasEmail: null,
                  hasPhone: null,
                  hasNotes: null,
                  lastContactDays: null,
                  dateFrom: '',
                  dateTo: '',
                  tagIds: [],
                });
                fetchLeads(searchQuery, {
                  source: 'all',
                  hasEmail: null,
                  hasPhone: null,
                  hasNotes: null,
                  lastContactDays: null,
                  dateFrom: '',
                  dateTo: '',
                  tagIds: [],
                });
              }}
              startIcon={<ClearIcon />}
            >
              Limpar Filtros
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {/* Filtro por Canal */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Canal</InputLabel>
              <Select
                value={advancedFilters.source}
                onChange={(e) => handleAdvancedFiltersChange({
                  ...advancedFilters,
                  source: e.target.value
                })}
                label="Canal"
              >
                <MenuItem value="all">Todos os canais</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="telegram">Telegram</MenuItem>
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="webchat">WebChat</MenuItem>
              </Select>
            </FormControl>

            {/* Filtro por Email */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Email</InputLabel>
              <Select
                value={advancedFilters.hasEmail === null ? 'all' : advancedFilters.hasEmail.toString()}
                onChange={(e) => handleAdvancedFiltersChange({
                  ...advancedFilters,
                  hasEmail: e.target.value === 'all' ? null : e.target.value === 'true'
                })}
                label="Email"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="true">Com email</MenuItem>
                <MenuItem value="false">Sem email</MenuItem>
              </Select>
            </FormControl>

            {/* Filtro por Telefone */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Telefone</InputLabel>
              <Select
                value={advancedFilters.hasPhone === null ? 'all' : advancedFilters.hasPhone.toString()}
                onChange={(e) => handleAdvancedFiltersChange({
                  ...advancedFilters,
                  hasPhone: e.target.value === 'all' ? null : e.target.value === 'true'
                })}
                label="Telefone"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="true">Com telefone</MenuItem>
                <MenuItem value="false">Sem telefone</MenuItem>
              </Select>
            </FormControl>

            {/* Filtro por Notas */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Notas</InputLabel>
              <Select
                value={advancedFilters.hasNotes === null ? 'all' : advancedFilters.hasNotes.toString()}
                onChange={(e) => handleAdvancedFiltersChange({
                  ...advancedFilters,
                  hasNotes: e.target.value === 'all' ? null : e.target.value === 'true'
                })}
                label="Notas"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="true">Com notas</MenuItem>
                <MenuItem value="false">Sem notas</MenuItem>
              </Select>
            </FormControl>

            {/* Filtro por Último Contato */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Último contato</InputLabel>
              <Select
                value={advancedFilters.lastContactDays || ''}
                onChange={(e) => handleAdvancedFiltersChange({
                  ...advancedFilters,
                  lastContactDays: e.target.value ? Number(e.target.value) : null
                })}
                label="Último contato"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={1}>Último dia</MenuItem>
                <MenuItem value={7}>Última semana</MenuItem>
                <MenuItem value={30}>Último mês</MenuItem>
                <MenuItem value={90}>Últimos 3 meses</MenuItem>
              </Select>
            </FormControl>

            {/* Filtros de Data */}
            <TextField
              size="small"
              type="date"
              label="Data inicial"
              InputLabelProps={{ shrink: true }}
              value={advancedFilters.dateFrom}
              onChange={(e) => handleAdvancedFiltersChange({
                ...advancedFilters,
                dateFrom: e.target.value
              })}
            />
            <TextField
              size="small"
              type="date"
              label="Data final"
              InputLabelProps={{ shrink: true }}
              value={advancedFilters.dateTo}
              onChange={(e) => handleAdvancedFiltersChange({
                ...advancedFilters,
                dateTo: e.target.value
              })}
            />
          </Box>
        </Card>
      )}

      {/* Barra de scroll acima dos cards de quantidade */}
      <Box 
        data-scroll-sync
        sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflowX: { xs: 'hidden', md: 'auto' },
          overflowY: 'hidden',
          mb: 0.5,
          height: '10px',
          position: 'relative',
          // Customização da barra de scroll teal elegante
          '&::-webkit-scrollbar': {
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(20, 184, 166, 0.1)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.8) 0%, rgba(13, 148, 136, 0.8) 100%)',
            borderRadius: '10px',
            border: '1px solid rgba(20, 184, 166, 0.3)',
            boxShadow: '0 2px 4px rgba(20, 184, 166, 0.2)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.9) 0%, rgba(13, 148, 136, 0.9) 100%)',
              boxShadow: '0 4px 8px rgba(20, 184, 166, 0.3)',
            }
          },
          '&::-webkit-scrollbar-thumb:active': {
            background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.9) 0%, rgba(13, 148, 136, 0.9) 100%)',
          },
          // Para Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(20, 184, 166, 0.8) rgba(20, 184, 166, 0.1)',
        }}
        onScroll={(e) => {
          if (scrollBarRef) {
            scrollBarRef.scrollLeft = e.currentTarget.scrollLeft;
          }
        }}
      >
        <Box sx={{
          width: { 
            xs: '100%', 
            md: kanbanColumns.length > 0 
              ? `${kanbanColumns.length * 250 + (kanbanColumns.length - 1) * 8 + 32}px`
              : '100%'
          },
          height: '1px',
        }} />
      </Box>

      {/* Container único com scroll sincronizado para métricas e kanban */}
      <Box 
        ref={setScrollBarRef}
        data-scroll-sync
        sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflowX: { xs: 'hidden', md: 'auto' },
          overflowY: 'hidden',
          mb: 0,
          position: 'relative',
          // Esconder a barra de scroll padrão, pois temos uma acima
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
        onScroll={(e) => {
          // Sincronizar com a barra acima
          const scrollBars = document.querySelectorAll('[data-scroll-sync]');
          scrollBars.forEach((bar: any) => {
            if (bar !== e.currentTarget) {
              bar.scrollLeft = e.currentTarget.scrollLeft;
            }
          });
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          width: { 
            xs: '100%', 
            md: kanbanColumns.length > 0 
              ? `${kanbanColumns.length * 250 + (kanbanColumns.length - 1) * 8 + 32}px`
              : '100%'
          },
          minWidth: { 
            xs: '100%', 
            md: kanbanColumns.length > 0 
              ? `${kanbanColumns.length * 250 + (kanbanColumns.length - 1) * 8 + 32}px`
              : '100%'
          },
        }}>
          {/* Cards de Métricas - Sincronizados com o kanban */}
          <Box 
            ref={setCardsContainerRef}
            sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 1, md: 0.5 }, 
              p: { xs: 1, md: 1 },
              width: '100%',
              overflowX: 'hidden',
              overflowY: 'hidden',
              position: 'relative',
            }}>
            {kanbanColumns.map((column, index) => (
              <Box 
                key={column.id} 
                sx={{ 
                  flex: '0 0 auto',
                  minWidth: { xs: '100%', md: '250px' },
                  width: { xs: '100%', md: '250px' },
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Card className="glass-blue-card" sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${column.color}80 0%, ${column.color}40 100%)`,
                    borderRadius: '12px 12px 0 0',
                  },
                }}>
                  <CardContent sx={{ 
                    textAlign: 'center', 
                    py: 2,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 600, 
                      color: column.color,
                      mb: 1
                    }}>
                      {column.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {column.title}
                    </Typography>
                    
                    {/* Valores totais por moeda */}
                    {(() => {
                      const totalBRL = column.contacts
                        .filter((c: any) => (c.currency || c.custom_fields?.currency || 'BRL') === 'BRL')
                        .reduce((sum: number, c: any) => sum + (c.value || 0), 0);
                      const totalUSD = column.contacts
                        .filter((c: any) => (c.currency || c.custom_fields?.currency || 'BRL') === 'USD')
                        .reduce((sum: number, c: any) => sum + (c.value || 0), 0);
                      
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                          {totalBRL > 0 && (
                            <Typography variant="caption" sx={{ 
                              color: column.color,
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              {formatCurrency(totalBRL, 'BRL')}
                            </Typography>
                          )}
                          {totalUSD > 0 && (
                            <Typography variant="caption" sx={{ 
                              color: column.color,
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              {formatCurrency(totalUSD, 'USD')}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            autoScroll={{
              threshold: {
                x: 0.2,
                y: 0.2,
              },
              acceleration: 10,
              interval: 5,
            }}
          >
            <Box 
              ref={setKanbanContainerRef}
              sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 1, md: 0.5 }, 
                mt: 0,
                p: { xs: 1, md: 1 },
                bgcolor: 'transparent',
                minHeight: { xs: 'auto', md: '500px' },
                width: '100%',
                overflowX: 'hidden',
                overflowY: { xs: 'auto', md: 'hidden' },
                position: 'relative',
                flexWrap: 'nowrap',
                flexShrink: 0,
                // Otimizações de performance
                willChange: 'transform',
                contain: 'layout style',
              }}>
                  {kanbanColumns.map((column, index) => (
          <DroppableColumn key={column.id} column={column}>
            {(isOver) => (
              <>
                {/* Header da Coluna */}
                <Box className="sophisticated-card" sx={{ 
                  p: 1.5, 
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                  overflow: 'hidden',
                  // Otimizações de performance
                  willChange: 'auto',
                  transform: 'translateZ(0)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: `linear-gradient(90deg, transparent, ${column.color}80, transparent)`,
                  },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: column.color
                      }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {column.title}
                    </Typography>
                    <Chip
                      label={column.contacts.length}
                      size="small"
                      sx={{
                        bgcolor: column.color + '15',
                        color: column.color,
                        fontWeight: 600,
                        minWidth: 20,
                        height: 18,
                        fontSize: '0.7rem'
                      }}
                    />
                    
                    {/* Valores totais por moeda */}
                    {(() => {
                      const totalBRL = column.contacts
                        .filter((c: any) => (c.currency || c.custom_fields?.currency || 'BRL') === 'BRL')
                        .reduce((sum: number, c: any) => sum + (c.value || 0), 0);
                      const totalUSD = column.contacts
                        .filter((c: any) => (c.currency || c.custom_fields?.currency || 'BRL') === 'USD')
                        .reduce((sum: number, c: any) => sum + (c.value || 0), 0);
                      
                      return (
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, ml: 3 }}>
                          {totalBRL > 0 && (
                            <Typography variant="caption" sx={{ 
                              color: column.color,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: column.color + '10',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1
                            }}>
                              {formatCurrency(totalBRL, 'BRL')}
                            </Typography>
                          )}
                          {totalUSD > 0 && (
                            <Typography variant="caption" sx={{ 
                              color: column.color,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: column.color + '10',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1
                            }}>
                              {formatCurrency(totalUSD, 'USD')}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                    
                    {/* Indicador de paginação */}
                    {column.contacts.length > INITIAL_CARDS_PER_COLUMN && (
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '0.65rem',
                        ml: 0.5,
                        opacity: 0.7
                      }}>
                        (Mostrando {Math.min(cardsPerColumn[column.id] || INITIAL_CARDS_PER_COLUMN, column.contacts.length)})
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    sx={{
                      color: column.color,
                      '&:hover': {
                        bgcolor: column.color + '15'
                      }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Lista de Leads */}
                <Box sx={{ 
                  flex: 1, 
                  p: 1,
                  overflow: 'auto',
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderTop: 'none',
                  borderBottomLeftRadius: 3,
                  borderBottomRightRadius: 3,
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
                  // Otimizações de performance
                  willChange: 'scroll-position',
                  transform: 'translateZ(0)', // Forçar aceleração de hardware
                  contain: 'layout style paint', // Isolar renderização
                  // Permitir scroll encadeado para o layout/página quando a coluna não rola
                  // ou está no topo/fundo — 'contain' bloqueava o wheel sobre os cards.
                  overscrollBehavior: 'auto',
                }}>
                  {column.contacts.length > 0 ? (
                    <SortableContext
                      items={getVisibleCards(column.contacts, column.id).map(contact => contact.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {getVisibleCards(column.contacts, column.id).map((contact) => (
                          <DraggableContactCard
                            key={contact.id}
                            contact={contact}
                            column={column}
                            onMoveToNextStage={moveToNextStage}
                            onContactClick={handleContactClick}
                          />
                        ))}
                        
                        {/* Botão para carregar mais cards se houver mais */}
                        {column.contacts.length > (cardsPerColumn[column.id] || INITIAL_CARDS_PER_COLUMN) && (
                          <Box sx={{ mt: 1, textAlign: 'center' }}>
                            <Button
                              className="sophisticated-button"
                              variant="outlined"
                              size="small"
                              onClick={() => loadMoreCards(column.id)}
                              disabled={isLoadingMore[column.id]}
                              sx={{ 
                                width: '100%',
                                fontSize: '0.75rem',
                                py: 0.5,
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                border: '1px solid rgba(139, 92, 246, 0.2)',
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1)',
                                color: 'text.primary',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(21, 101, 192, 0.15) 100%)',
                                  borderColor: 'rgba(25, 118, 210, 0.4)',
                                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.2), 0 2px 6px rgba(25, 118, 210, 0.15)',
                                  transform: 'translateY(-1px)',
                                }
                              }}
                            >
                              {isLoadingMore[column.id] ? (
                                <CircularProgress size={16} />
                              ) : (
                                `Ver mais (${column.contacts.length - (cardsPerColumn[column.id] || INITIAL_CARDS_PER_COLUMN)} restantes)`
                              )}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </SortableContext>
                  ) : (
                    <Box sx={{ flex: 1, minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <EmptyColumnArea isOver={isOver} />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </DroppableColumn>
        ))}
        
        {/* Indicador de Estágios - Botão Flutuante */}
        {showStageIndicator && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              right: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 1000
            }}
          >
            {/* Botão para mostrar todos os estágios */}
            <Button
              variant="contained"
              size="small"
              onClick={showAllStages}
              sx={{
                minWidth: 'auto',
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 8px 24px rgba(25, 118, 210, 0.4)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {kanbanColumns.length}
              </Typography>
            </Button>

            {/* Botões de navegação */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={scrollToPreviousStage}
                sx={{
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                onClick={scrollToNextStage}
                sx={{
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Box>
        )}
        </Box>

        {/* Drag Overlay - Ultra simplificado para máxima fluidez */}
        <DragOverlay 
          dropAnimation={null}
          style={{
            cursor: 'grabbing',
          }}
        >
          {draggedContact && (
            <Card
              sx={{
                width: 240,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                p: 1.5,
                opacity: 1,
                // Máxima otimização de performance
                willChange: 'transform',
                transform: 'translateZ(0) rotate(2deg)',
                border: '2px solid rgba(156, 39, 176, 0.5)',
                // Remover todos os efeitos pesados
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              <CardContent sx={{ p: '0 !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    src={draggedContact.avatar || undefined}
                    sx={{
                      bgcolor: draggedContact.avatar ? 'transparent' : '#6B7280' + '15',
                      color: 'text.secondary',
                      fontWeight: 600,
                      width: 32,
                      height: 32,
                      fontSize: '0.8rem'
                    }}
                  >
                    {!draggedContact.avatar && (draggedContact.name || draggedContact.email || draggedContact.phone || 'Lead').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                      {draggedContact.name || draggedContact.email || draggedContact.phone || 'Lead'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {(draggedContact.email || draggedContact.phone || 'Lead').slice(0, 20)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </DragOverlay>
      </DndContext>
        </Box>
      </Box>

      {/* Dialog para Adicionar Contato */}
      <Dialog open={openContactDialog} onClose={() => setOpenContactDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Novo Lead</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome"
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Telefone"
              value={contactForm.phone}
              onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Valor Estimado"
              type="number"
              value={contactForm.value}
              onChange={(e) => setContactForm({ ...contactForm, value: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: 'R$',
              }}
            />
            <FormControl fullWidth>
              <InputLabel id="crm-new-lead-pipeline-label">Pipeline</InputLabel>
              <Select
                labelId="crm-new-lead-pipeline-label"
                label="Pipeline"
                value={contactForm.pipelineName}
                onChange={(e) => {
                  const name = String(e.target.value);
                  const pipe = pipelines.find((p: any) => p.name === name);
                  const sorted = pipe?.stages?.length
                    ? sortStagesForPicker(stagesScopedToPipeline(pipe))
                    : [];
                  const firstId = sorted[0]?.id || '';
                  setContactForm({ ...contactForm, pipelineName: name, stage: firstId });
                }}
              >
                {pipelines.map((p: any) => (
                  <MenuItem key={p.id} value={p.name}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="crm-new-lead-stage-label">Estágio</InputLabel>
              <Select
                labelId="crm-new-lead-stage-label"
                label="Estágio"
                value={contactForm.stage}
                onChange={(e) => setContactForm({ ...contactForm, stage: e.target.value })}
                disabled={!newLeadPipelineStages.length}
              >
                {newLeadPipelineStages.map((s: any) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={crmCatalogTags}
              getOptionLabel={(o) => o.name}
              value={crmCatalogTags.filter((t) => contactForm.tagIds.includes(t.id))}
              onChange={(_, v) =>
                setContactForm((prev) => ({ ...prev, tagIds: v.map((x) => x.id) }))
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Selecione ou crie depois no lead" />
              )}
            />
            <TextField
              label="Notas"
              multiline
              rows={3}
              value={contactForm.notes}
              onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContactDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveContact} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openNewPipelineDialog}
        onClose={() => !creatingPipeline && setOpenNewPipelineDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nova pipeline</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome"
              value={newPipelineName}
              onChange={(e) => setNewPipelineName(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Descrição (opcional)"
              value={newPipelineDescription}
              onChange={(e) => setNewPipelineDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Cor (hex)"
              value={newPipelineColor}
              onChange={(e) => setNewPipelineColor(e.target.value)}
              placeholder="#3B82F6"
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Serão criados automaticamente os estágios padrão (Novo Lead até Fechamento). Depois você pode
              renomear cada um pelo ícone de edição ao lado do seletor de pipeline.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewPipelineDialog(false)} disabled={creatingPipeline}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleCreatePipeline()} disabled={creatingPipeline}>
            {creatingPipeline ? 'Criando…' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditPipelineStagesDialog}
        onClose={() => !savingPipelineStageNames && setOpenEditPipelineStagesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar estágios — {selectedPipelineName || 'Pipeline'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Os nomes aparecem nas colunas do Kanban e nos detalhes do lead. A ordem dos estágios não é alterada
            aqui.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            {pipelineStagesEditRows.map((row) => (
              <TextField
                key={row.id}
                label={`Estágio (ordem ${row.stage_order})`}
                value={pipelineStageNameDrafts[row.id] ?? ''}
                onChange={(e) =>
                  setPipelineStageNameDrafts((prev) => ({
                    ...prev,
                    [row.id]: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                disabled={savingPipelineStageNames}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditPipelineStagesDialog(false)} disabled={savingPipelineStageNames}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSaveEditPipelineStages()}
            disabled={savingPipelineStageNames}
          >
            {savingPipelineStageNames ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Detalhes do Lead */}
      <Dialog 
        open={openLeadDetailsDialog} 
        onClose={handleCloseLeadDetails} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #14b8a6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(20, 184, 166, 0.2)',
            overflow: 'hidden',
            '&:hover': {
              background: '#ffffff',
              border: '2px solid #14b8a6',
              boxShadow: '0 4px 16px rgba(20, 184, 166, 0.2)',
            }
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#14b8a6',
          color: '#ffffff',
          p: 3,
          borderBottom: '2px solid #0d9488'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={selectedContact?.avatar || undefined}
              sx={{
                bgcolor: selectedContact?.avatar ? 'transparent' : '#14b8a6',
                color: '#ffffff',
                fontWeight: 700,
                width: 56,
                height: 56,
                fontSize: '1.4rem',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                boxShadow: '0 4px 16px rgba(139, 92, 246, 0.2)'
              }}
            >
              {!selectedContact?.avatar && (selectedContact?.name || selectedContact?.email || selectedContact?.phone || 'Lead').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                color: '#ffffff',
                letterSpacing: '-0.025em'
              }}>
                {selectedContact?.name || 'Lead'}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                mt: 1,
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: 2,
                p: 1.5,
                width: 'fit-content'
              }}>
                {selectedContact?.source && getChannelIcon(selectedContact.source)}
                <Typography variant="body1" sx={{ 
                  fontWeight: 500,
                  color: '#ffffff',
                  textTransform: 'capitalize'
                }}>
                  {selectedContact?.source?.charAt(0).toUpperCase() + selectedContact?.source?.slice(1)} Lead
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 3,
          backgroundColor: '#ffffff'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 1 }}>
            {/* Informações Básicas */}
            <Box sx={{
              p: 3,
              backgroundColor: '#ffffff',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#ffffff',
                border: '1px solid rgba(139, 92, 246, 0.2)',
              }
            }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                fontWeight: 600, 
                color: '#000000',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)'
                }} />
                Informações Básicas
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Box sx={{
                  background: 'rgba(139, 92, 246, 0.08)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: 2,
                  p: 2
                }}>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    color: '#64748b',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em'
                  }}>
                    Nome
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600,
                    color: '#000000'
                  }}>
                    {selectedContact?.name || 'Não informado'}
                  </Typography>
                </Box>
                <Box sx={{
                  background: 'rgba(139, 92, 246, 0.08)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: 2,
                  p: 2
                }}>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    color: '#64748b',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em'
                  }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600,
                    color: '#000000'
                  }}>
                    {selectedContact?.email || 'Não informado'}
                  </Typography>
                </Box>
                <Box sx={{
                  background: 'rgba(139, 92, 246, 0.08)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: 2,
                  p: 2
                }}>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    color: '#64748b',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em'
                  }}>
                    Telefone
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 600,
                    color: '#000000'
                  }}>
                    {selectedContact?.phone || 'Não informado'}
                  </Typography>
                </Box>
                <Box sx={{
                  background: 'rgba(139, 92, 246, 0.08)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: 2,
                  p: 2
                }}>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    color: '#64748b',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em'
                  }}>
                    Canal
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {selectedContact?.source && getChannelIcon(selectedContact.source)}
                    <Typography variant="body1" sx={{ 
                      fontWeight: 600,
                      color: '#000000',
                      textTransform: 'capitalize'
                    }}>
                      {selectedContact?.source?.charAt(0).toUpperCase() + selectedContact?.source?.slice(1)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: '#000000' }}>
                Tags
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Mesmas tags do catálogo usadas no WhatsApp e no inbox.
              </Typography>
              <LeadContactTags
                contactId={selectedContact?.id}
                onAssignmentsChanged={() =>
                  fetchLeads(
                    searchQuery,
                    { ...advancedFilters, source: channelFilter },
                    { pipeline: pipelineNameRef.current || selectedPipelineName }
                  )
                }
              />
            </Box>

            {/* IDs Específicos do Canal */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
                IDs do Canal
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {selectedContact?.source === 'telegram' && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#64748b' }}>
                      Telegram ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', color: '#000000' }}>
                      {selectedContact?.custom_fields?.telegram_id || 'Não disponível'}
                    </Typography>
                  </Box>
                )}
                {selectedContact?.source === 'whatsapp' && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#64748b' }}>
                      WhatsApp ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', color: '#000000' }}>
                      {selectedContact?.custom_fields?.whatsapp_id || 'Não disponível'}
                    </Typography>
                  </Box>
                )}
                {selectedContact?.source === 'instagram' && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#64748b' }}>
                      Instagram ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', color: '#000000' }}>
                      {selectedContact?.custom_fields?.instagram_id || 'Não disponível'}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#64748b' }}>
                    ID Interno
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', color: '#000000' }}>
                    {selectedContact?.id || 'Não disponível'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Produtos Comprados */}
            {(() => {
              const purchasedProducts = selectedContact?.custom_fields?.purchased_products;
              const isValid = purchasedProducts && Array.isArray(purchasedProducts) && purchasedProducts.length > 0;
              return isValid;
            })() && (
              <Box>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  fontWeight: 600, 
                  color: '#000000',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }} />
                  Produtos Comprados
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1.5 
                }}>
                  {selectedContact?.custom_fields?.purchased_products?.map((product: string, index: number) => (
                    <Chip
                      key={index}
                      label={product}
                      sx={{
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        color: '#10B981',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Datas */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
                Datas
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#64748b' }}>
                    Data da Primeira Mensagem
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                    {selectedContact?.created_at ? new Date(selectedContact.created_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Não disponível'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#64748b' }}>
                    Último Contato
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                    {selectedContact?.lastContact ? new Date(selectedContact.lastContact).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Não disponível'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Estágio Atual */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
                Estágio do Lead
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={getStageName(selectedContact?.crm_stages?.stage_order, stagesConfig) || selectedContact?.crm_stages?.name || 'Não definido'}
                    sx={{
                      bgcolor: selectedContact?.crm_stages?.color ? `${selectedContact.crm_stages.color}15` : 'rgba(139, 92, 246, 0.1)',
                      color: selectedContact?.crm_stages?.color || '#8b5cf6',
                      fontWeight: 600,
                      border: `1px solid ${selectedContact?.crm_stages?.color ? selectedContact.crm_stages.color + '40' : 'rgba(139, 92, 246, 0.2)'}`
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Ordem: {selectedContact?.crm_stages?.stage_order || 'N/A'}
                  </Typography>
                </Box>
                
                {/* Seletor de Novo Estágio */}
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#8b5cf6' } }}>Mover para novo estágio</InputLabel>
                  <Select
                    value={selectedStageId}
                    onChange={(e) => {
                      const newStageId = e.target.value as string;
                      setSelectedStageId(newStageId);
                      if (newStageId && newStageId !== selectedContact?.crm_stages?.id) {
                        handleStageChange(newStageId);
                      }
                    }}
                    label="Mover para novo estágio"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(139, 92, 246, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(139, 92, 246, 0.3)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#14b8a6',
                      },
                      '& .MuiSelect-select': {
                        color: '#000000',
                        backgroundColor: 'rgba(139, 92, 246, 0.08)',
                      },
                    }}
                  >
                    {/* Renderizar opções de estágios */}
                    {allStages.length > 0 ? (
                      allStages.map((stage: any) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: stage.color || '#6B7280' 
                              }} 
                            />
                            <Typography>{getStageName(stage.stage_order, stagesConfig) || stage.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              (Ordem {stage.stage_order})
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography sx={{ color: '#64748b' }}>Nenhum estágio disponível</Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Notas */}
            {selectedContact?.notes && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
                  Notas
                </Typography>
                <Typography variant="body1" sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(139, 92, 246, 0.08)', 
                  borderRadius: 2,
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  color: '#000000'
                }}>
                  {selectedContact.notes}
                </Typography>
              </Box>
            )}

            {/* Campos Customizados */}
            {selectedContact?.custom_fields && Object.keys(selectedContact.custom_fields).length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
                  Campos Customizados
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {Object.entries(selectedContact.custom_fields).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#64748b' }}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', color: '#000000' }}>
                        {String(value) || 'Não disponível'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{
          borderTop: '1px solid rgba(139, 92, 246, 0.2)',
          p: 3
        }}>
          <Button 
            onClick={handleCloseLeadDetails}
            sx={{
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3), 0 2px 8px rgba(20, 184, 166, 0.2)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #0d9488 0%, #047857 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(20, 184, 166, 0.4), 0 4px 12px rgba(20, 184, 166, 0.3)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontWeight: 600,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              borderRadius: 2
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CRM; 